import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import type { ApplyBrandResult, BrandColors, BrandSpec, PlaceholderFormat, ThemeColors } from "../../../types";
import { useBrandExtractor } from "../hooks/useBrandExtractor";
import { useBrandApply, type ApplyState } from "../hooks/useBrandApply";
import { useMyBrandTemplates } from "../hooks/useMyBrandTemplates";
import {
  brandMasters, buildBrandSpecFromThemeColors, extractBrand, readMastersFromBuffer, type MasterInfo,
} from "../services/brandExtractor";
import {
  deleteTemplateFile, downloadFileName, getTemplateFile, putTemplateFile, type MyBrandTemplate,
} from "../services/myBrandTemplatesStorage";
import {
  buildPaletteGrid, clampNumber, formatRgbHex, hslPartsToRgb, hsvToRgb, isValidHex, normalizeHex, parseHexRgb,
  rgbToHslParts, rgbToHsv, toHex6, validateColourField, type ColourField,
} from "../../../utils/colourUtils";
import { downloadBytes, getActivePresentationBuffer, goToSlide, PPTX_MIME_TYPE } from "../../../utils/officePptx";
import {
  findMissingFonts, formatFontList, loadFontData, type FontOption,
} from "../../FormatTools/services/fontData";
import { logEvent, logOfficeError } from "../../../../../telemetry";
import { fetchSavedBrandTemplates, type SavedBrandTemplate } from "../services/brandTemplatesApi";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import {
  consumePendingSavedTemplateSelection, subscribePendingSavedTemplateSelection, subscribePreviewedTemplateUnavailable,
} from "../services/pendingSavedTemplateSelection";
import {
  APPLIED_NOTE, ConfirmDialog, MoreMenu, SavedTemplatesTab, SelectedNote, truncateName, type SavedTemplateSelectionStatus,
} from "./SavedTemplatesTab";
import styles from "./ApplyBrandPanel.module.css";

type SourceTab = "active" | "upload" | "saved";

/** Advanced Options (BRD FR-47): only Keep rounded corners / shadows / animations are exposed;
 *  the rest stay off or on as before (Fix alignment and Autofit are excluded from this BRD). */
export type AdvancedOpts = CustomiseAdvanced;

const DEFAULT_ADVANCED_OPTIONS: AdvancedOpts = {
  allowShadows: true, isAllowGradients: true, allowRoundedCorners: true, allowAnimations: true,
  compressImages: false, isFixAlignment: false, autofitSlideContent: false,
};

/** Intro message per tab (BRD FR-02). */
const INTRO_MESSAGE: Record<SourceTab, { title: string; text: string }> = {
  saved: { title: "Make this presentation match a brand", text: "Pick a brand below, then press Apply Brand. That's it." },
  active: { title: "Match your slides to this deck's theme", text: "Check the theme below, then press Apply Brand. That's it." },
  upload: { title: "Use a template from your files", text: "Upload a .potx or .pptx below, then press Apply Brand. That's it." },
};

/** Tooltip shown on a disabled Apply button (BRD FR-55). */
const DISABLED_REASON: Record<SourceTab, string> = {
  saved: "Pick a template in this tab to continue",
  active: "No active theme to apply. Choose a brand template to continue",
  upload: "Upload a template to continue",
};

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const HIGHLIGHT_MS = 4000;


/** What is currently selected (BR-01: only one source at a time, and Apply only uses
 *  a selection that belongs to the tab on screen). */
interface ActiveSource {
  readonly tab: SourceTab;
  readonly kind: CustomiseSourceKind;
  /** Unique across sources: org:<id>, local:<id>, theme:<masterId>, upload:<uploadId>:<masterId>. */
  readonly key: string;
  readonly name: string;
  readonly localId?: string;
}

interface Remembered {
  readonly active: ActiveSource;
  readonly brand: BrandSpec;
}

interface PendingCustomise {
  readonly key: string;
  readonly missing?: readonly StyleSection[];
}

interface ApplyBrandPanelProps {
  readonly onBack?: () => void;
}

/** Usage telemetry for the apply, upload, save, rename and delete actions (BRD NFR-10). */
function logApplyBrandUsage(reason: string): void {
  logEvent("connector.action.invoke", "usage", { connectorSystemName: "deckorate", reason });
}

function swatchesOf(brand: BrandSpec): string[] {
  return [
    brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
    brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
    brand.colors.dk1, brand.colors.dk2,
  ].map((c) => normalizeHex(c)).filter((c): c is string => c !== null);
}

function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`; // NOSONAR typescript:S2245
}

function fileTypeLabel(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const known: Record<string, string> = {
    pdf: "PDF", doc: "Word document", docx: "Word document", xls: "Excel workbook", xlsx: "Excel workbook",
    txt: "text file", png: "PNG image", jpg: "JPEG image", jpeg: "JPEG image", zip: "ZIP archive",
  };
  return known[ext] ?? (ext ? `.${ext} file` : "file of an unknown type");
}

/** A name that doesn't clash with `taken`, adding " (2)", " (3)" … before the extension. */
function nextFreeName(name: string, taken: readonly string[]): string {
  const lower = new Set(taken.map((t) => t.toLowerCase()));
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let n = 2;
  while (lower.has(`${stem} (${n})${ext}`.toLowerCase())) n++;
  return `${stem} (${n})${ext}`;
}

/** An OLE compound file (legacy .ppt or an encrypted/password-protected package) — not a zip. */
function looksPasswordProtected(buffer: ArrayBuffer): boolean {
  const head = new Uint8Array(buffer, 0, Math.min(8, buffer.byteLength));
  return head.length >= 4 && head[0] === 0xd0 && head[1] === 0xcf && head[2] === 0x11 && head[3] === 0xe0;
}

/** The saved-template id behind an "org:" or "local:" source key, or null for any other key. */
function savedIdFromKey(key: string | null): string | null {
  if (key?.startsWith("org:")) return key.slice("org:".length);
  if (key?.startsWith("local:")) return key.slice("local:".length);
  return null;
}

const SAVED_STATUS: Partial<Record<string, SavedTemplateSelectionStatus>> = {
  extracting: "extracting", done: "ready", error: "error",
};

/** The Saved Templates tab's view of the current selection's extraction. */
function savedTemplateStatus(active: ActiveSource | null, extractStatus: string): SavedTemplateSelectionStatus {
  if (active?.tab !== "saved") return "idle";
  return SAVED_STATUS[extractStatus] ?? "idle";
}

/** The intro note shows until dismissed, and only while its tab has nothing to show yet. */
function isIntroVisible(
  tab: SourceTab, dismissed: ReadonlySet<SourceTab>, active: ActiveSource | null, deck: DeckLoadState, uploadCount: number,
): boolean {
  if (dismissed.has(tab)) return false;
  if (tab === "saved") return active?.tab !== "saved";
  if (tab === "active") return deck.status !== "ready" || brandMasters(deck.masters).length > 0;
  return uploadCount === 0;
}

interface SelectionView {
  /** Saved Templates: the selected brand or Your Template id. */
  readonly savedId: string | null;
  /** Saved Templates: the brand template with Customise changes for the next apply. */
  readonly customisedOrgId: string | null;
  readonly appliedSavedId: string | null;
  readonly appliedMasterId: string | null;
  /** Upload: the selected upload/theme key. */
  readonly uploadKey: string | null;
}

/** The per-tab selection and Applied ids the source tabs render from. */
function selectionView(active: ActiveSource | null, appliedKey: string | null, hasEdits: (key: string) => boolean): SelectionView {
  let savedId: string | null = null;
  if (active?.tab === "saved") savedId = active.localId ?? active.key.slice("org:".length);
  const customisedOrgId = active?.kind === "brand" && hasEdits(active.key) ? active.key.slice("org:".length) : null;
  return {
    savedId,
    customisedOrgId,
    appliedSavedId: savedIdFromKey(appliedKey),
    appliedMasterId: appliedKey?.startsWith("theme:") ? appliedKey.slice("theme:".length) : null,
    uploadKey: active?.tab === "upload" ? active.key : null,
  };
}

/** Warns that a brand template's fonts aren't installed on this device (shown above Apply). */
function OrgFontWarning({ fonts }: Readonly<{ fonts: readonly string[] }>) {
  if (fonts.length === 0) return null;
  const plural = fonts.length > 1;
  return (
    <BrandAlert tone="warning">
      <b>{fonts.join(", ")}</b> {plural ? "aren't" : "isn't"} installed on this device.
      This brand template uses {plural ? "them" : "it"}. PowerPoint will show a substitute until it&rsquo;s
      installed. Ask your IT team to install it.
    </BrandAlert>
  );
}

export function ApplyBrandPanel({ onBack }: Readonly<ApplyBrandPanelProps>) {
  const [sourceTab, setSourceTab] = useState<SourceTab>("saved");
  const [scope, setScope] = useState<Scope>("slide");
  const [saveTarget, setSaveTarget] = useState<SaveTarget>("current");
  const [dismissedIntros, setDismissedIntros] = useState<ReadonlySet<SourceTab>>(new Set());
  const [notice, setNotice] = useState<BarNotice | null>(null);
  const [highlightYours, setHighlightYours] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [active, setActive] = useState<ActiveSource | null>(null);
  // Customise changes per source key: brand template changes last for one apply (BR-02);
  // deck theme / upload / Your Template changes are kept and used for every later apply (BR-03).
  const [edits, setEdits] = useState<Record<string, { brand: BrandSpec; advanced: AdvancedOpts }>>({});
  const [addedStyles, setAddedStyles] = useState<Record<string, readonly StyleSection[]>>({});
  const [customising, setCustomising] = useState<{ missing?: readonly StyleSection[] } | null>(null);
  const [pendingCustomise, setPendingCustomise] = useState<PendingCustomise | null>(null);
  const [appliedKey, setAppliedKey] = useState<string | null>(null);
  const tabMemoryRef = useRef<Partial<Record<SourceTab, Remembered>>>({});

  // This Deck's Theme
  const [deck, setDeck] = useState<DeckLoadState>({ status: "loading" });
  const deckBufferRef = useRef<ArrayBuffer | null>(null);
  // The deck is read once and reused when switching back to the tab; it is read again
  // only after an apply has changed it.
  const deckStaleRef = useRef(false);
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [removeOthers, setRemoveOthers] = useState(false);
  const [savedKeys, setSavedKeys] = useState<ReadonlySet<string>>(new Set());
  const [freshlyRead, setFreshlyRead] = useState<string | null>(null);

  // Upload
  const [uploads, setUploads] = useState<readonly UploadEntry[]>([]);
  const [reading, setReading] = useState<string | null>(null);
  const [problem, setProblem] = useState<UploadProblem | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicatePrompt | null>(null);
  const [renameRequestId, setRenameRequestId] = useState<string | null>(null);
  const lastFileRef = useRef<File | null>(null);
  const pendingDuplicateRef = useRef<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const brandCacheRef = useRef<Map<string, BrandSpec>>(new Map());

  const { state: extractState, extractFromBuffer, extractFromUrls, setDirectBrand, reset: resetExtract } = useBrandExtractor();
  const { state: applyState, apply, cancel: cancelApply, reset: resetApply } = useBrandApply();
  const {
    templates: myTemplates, save: saveMyTemplate, remove: removeMyTemplate,
    rename: renameMyTemplate, update: updateMyTemplate,
  } = useMyBrandTemplates();

  const http = useDeckorateHttp();
  const baseBrand: BrandSpec | null = extractState.status === "done" ? extractState.brand : null;
  const activeEdit = active ? edits[active.key] : undefined;
  const finalBrand = activeEdit?.brand ?? baseBrand;
  const finalAdvanced = activeEdit?.advanced ?? DEFAULT_ADVANCED_OPTIONS;
  const onThisTab = active !== null && active.tab === sourceTab;

  const clearFeedback = useCallback(() => {
    setNotice(null);
    resetApply();
  }, [resetApply]);

  // ── Selection ──────────────────────────────────────────────────────────────

  const selectSavedTemplate = useCallback((template: SavedBrandTemplate) => {
    // Prefer building the brand synchronously from themeColors; only fall
    // back to downloading + parsing the .pptx when there's no themeColors.
    if (!template.themeColors && template.sourceFileUrlCandidates.length === 0) return;
    clearFeedback();
    setActive({ tab: "saved", kind: "brand", key: `org:${template.id}`, name: template.name });
    if (template.themeColors) {
      setDirectBrand(buildBrandSpecFromThemeColors(template.themeColors), template.name);
    } else {
      extractFromUrls(template.sourceFileUrlCandidates, template.name).catch((e) => logOfficeError(e, "apply-brand-extract-saved"));
    }
  }, [clearFeedback, extractFromUrls, setDirectBrand]);

  const selectMyTemplate = useCallback((template: MyBrandTemplate) => {
    clearFeedback();
    setActive({ tab: "saved", kind: "yours", key: `local:${template.id}`, name: template.name, localId: template.id });
    setDirectBrand(template.brand, template.name);
  }, [clearFeedback, setDirectBrand]);

  const selectMaster = useCallback((master: MasterInfo, fresh = true) => {
    const buffer = deckBufferRef.current;
    if (!buffer) return;
    clearFeedback();
    setSelectedMasterId(master.id);
    setActive({ tab: "active", kind: "theme", key: `theme:${master.id}`, name: master.name });
    if (fresh) setFreshlyRead(`theme:${master.id}`);
    extractFromBuffer(buffer, master.name, { themePath: master.themePath, masterPath: master.masterPath })
      .catch((e) => logOfficeError(e, "apply-brand-extract-active"));
  }, [clearFeedback, extractFromBuffer]);

  const selectUpload = useCallback((upload: UploadEntry, theme: { id: string; name: string; themePath: string; masterPath: string }) => {
    clearFeedback();
    const key = uploadKey(upload.id, theme.id);
    setActive({ tab: "upload", kind: "upload", key, name: upload.themes.length > 1 ? theme.name : upload.name });
    setFreshlyRead(key);
    const cached = brandCacheRef.current.get(key);
    if (cached) {
      setDirectBrand(cached, upload.name);
      return;
    }
    extractFromBuffer(upload.buffer, upload.name, { themePath: theme.themePath, masterPath: theme.masterPath })
      .catch((e) => logOfficeError(e, "apply-brand-extract-upload"));
  }, [clearFeedback, extractFromBuffer, setDirectBrand]);

  // Remember each extracted brand so the cache can answer later selections / saves.
  useEffect(() => {
    if (active?.kind === "upload" && baseBrand) brandCacheRef.current.set(active.key, baseBrand);
  }, [active, baseBrand]);

  // The Asset Library's Brand Templates tab routes a pick here instead of
  // doing anything itself — see pendingSavedTemplateSelection.ts. Applied
  // once on mount (a later, ordinary visit to Apply Brand must not replay a
  // stale pick, which is exactly what consuming — not just reading — achieves)
  // and again live via the subscription below, for a pick made (e.g. Switch
  // Template from the Brand Templates preview) while this panel is already
  // open and mounted, where the mount effect alone would never re-fire.
  const applyPendingSavedTemplateSelection = useCallback(() => {
    const pending = consumePendingSavedTemplateSelection();
    if (!pending) return;
    setSourceTab("saved");
    if (pending.kind === "org") selectSavedTemplate(pending.template);
    else selectMyTemplate(pending.template);
  }, [selectSavedTemplate, selectMyTemplate]);

  useEffect(() => {
    applyPendingSavedTemplateSelection();
  }, [applyPendingSavedTemplateSelection]);

  useEffect(
    () => subscribePendingSavedTemplateSelection(applyPendingSavedTemplateSelection),
    [applyPendingSavedTemplateSelection],
  );

  // ── This Deck's Theme: read the open deck's masters ─────────────────────────

  const loadDeck = useCallback(async () => {
    setDeck({ status: "loading" });
    deckStaleRef.current = false;
    try {
      const buffer = await getActivePresentationBuffer();
      deckBufferRef.current = buffer;
      const masters = readMastersFromBuffer(buffer);
      if (masters === null) {
        setDeck({ status: "error", message: "The file couldn't be read. It may be corrupt or password-protected." });
        return;
      }
      setDeck({ status: "ready", masters });
    } catch (e) {
      logOfficeError(e, "apply-brand-read-deck");
      setDeck({ status: "error", message: e instanceof Error ? e.message : "Something went wrong reading the presentation." });
    }
  }, []);

  // Opening This Deck's Theme always selects the deck's theme (FR-05): the
  // masters are read, then the remembered (or first brand) master is selected.
  const deckReady = deck.status === "ready" ? deck.masters : null;
  useEffect(() => {
    if (sourceTab !== "active" || !deckReady) return;
    if (active?.tab === "active") return;
    const brand = brandMasters(deckReady);
    const pick = brand.find((m) => m.id === selectedMasterId) ?? brand[0];
    if (pick) selectMaster(pick);
  }, [sourceTab, deckReady, active?.tab]);

  // ── Tabs ───────────────────────────────────────────────────────────────────

  const handleTabChange = useCallback((tab: SourceTab) => {
    if (tab === sourceTab) return;
    clearFeedback();
    setCustomising(null);
    setPendingCustomise(null);
    // Remember what this tab had selected so returning restores it (FR-05).
    if (active?.tab === sourceTab && baseBrand) {
      tabMemoryRef.current[sourceTab] = { active, brand: baseBrand };
    }
    resetExtract();
    setActive(null);
    setSourceTab(tab);

    if (tab === "active") {
      if (deck.status === "ready" && deckBufferRef.current && !deckStaleRef.current) {
        // Already read: restore the last selection without reading the deck again.
        const remembered = tabMemoryRef.current.active;
        if (remembered) {
          setActive(remembered.active);
          setDirectBrand(remembered.brand, remembered.active.name);
        }
        return;
      }
      loadDeck().catch((e) => logOfficeError(e, "apply-brand-read-deck"));
      return;
    }
    const remembered = tabMemoryRef.current[tab];
    if (!remembered) return;
    // An upload removed since is not restored (its memory is dropped on removal).
    setActive(remembered.active);
    setDirectBrand(remembered.brand, remembered.active.name);
  }, [sourceTab, active, baseBrand, deck.status, clearFeedback, resetExtract, setDirectBrand, loadDeck]);

  const goToYourTemplates = useCallback(() => {
    handleTabChange("saved");
    setHighlightYours(true);
    setTimeout(() => setHighlightYours(false), HIGHLIGHT_MS);
  }, [handleTabChange]);

  // ── Customise ──────────────────────────────────────────────────────────────

  // Choosing Customise selects the item first, then opens Customise once its brand is ready.
  useEffect(() => {
    if (!pendingCustomise || !active || active.key !== pendingCustomise.key || !baseBrand) return;
    setCustomising({ missing: pendingCustomise.missing });
    setPendingCustomise(null);
  }, [pendingCustomise, active, baseBrand]);

  // The library changed and the selected brand template is gone: clear the selection (EC-05).
  const clearUnavailableSelection = useCallback(() => {
    delete tabMemoryRef.current.saved;
    setActive((a) => (a?.tab === "saved" && a.kind === "brand" ? null : a));
    resetExtract();
  }, [resetExtract]);

  // The Brand Templates preview (opened from the eye icon) noticed the
  // organisation withdrew the template it was showing (EC-08): by the time
  // the preview closes, the pane has already cleared the selection and shown
  // the same "no longer available" message EC-04 uses at apply time.
  useEffect(() => subscribePreviewedTemplateUnavailable(() => {
    clearUnavailableSelection();
    setNotice({
      tone: "error",
      title: "That brand template is no longer available.",
      text: "Your organisation updated the library, so nothing was changed. Choose another one instead.",
    });
  }), [clearUnavailableSelection]);

  const customiseSavedTemplate = useCallback((sel: { kind: "org"; template: SavedBrandTemplate } | { kind: "local"; template: MyBrandTemplate }) => {
    if (sel.kind === "org") {
      selectSavedTemplate(sel.template);
      setPendingCustomise({ key: `org:${sel.template.id}` });
    } else {
      selectMyTemplate(sel.template);
      setPendingCustomise({ key: `local:${sel.template.id}` });
    }
  }, [selectSavedTemplate, selectMyTemplate]);

  const stillMissing: StyleSection[] = useMemo(() => {
    if (!active || active.kind === "brand" || !finalBrand) return [];
    const added = addedStyles[active.key] ?? [];
    return (finalBrand.missingStyles ?? []).filter((s) => !added.includes(s));
  }, [active, finalBrand, addedStyles]);

  const openAddMissing = useCallback(() => {
    if (active) setCustomising({ missing: stillMissing });
  }, [active, stillMissing]);

  const finishCustomise = useCallback((brand: BrandSpec, advanced: AdvancedOpts, added: StyleSection[], mode: "use" | "save") => {
    if (!active) return;
    logApplyBrandUsage(mode === "use" ? "apply-brand-customise-used" : "apply-brand-customise-saved");
    const allAdded = [...new Set([...(addedStyles[active.key] ?? []), ...added])];
    const nextBrand: BrandSpec = { ...brand, missingStyles: (brand.missingStyles ?? []).filter((s) => !allAdded.includes(s)) };
    setAddedStyles((prev) => ({ ...prev, [active.key]: allAdded }));
    setCustomising(null);
    setNotice(null);

    if (mode === "use") {
      setEdits((prev) => ({ ...prev, [active.key]: { brand: nextBrand, advanced } }));
      setNotice({ tone: "info", text: "Changes will be used for this apply." });
      return;
    }
    if (active.kind === "yours" && active.localId) {
      const updated = updateMyTemplate(active.localId, nextBrand, swatchesOf(nextBrand));
      if (updated) {
        setDirectBrand(updated.brand, updated.name);
        setEdits((prev) => ({ ...prev, [active.key]: { brand: updated.brand, advanced } }));
      }
      return;
    }
    setEdits((prev) => ({ ...prev, [active.key]: { brand: nextBrand, advanced } }));
  }, [active, addedStyles, updateMyTemplate, setDirectBrand]);

  // ── Your Templates actions ─────────────────────────────────────────────────

  const handleRenameMyTemplate = useCallback((id: string, name: string) => {
    const outcome = renameMyTemplate(id, name);
    if (outcome === "renamed") {
      logApplyBrandUsage("apply-brand-template-renamed");
      setNotice({ tone: "success", text: `Renamed to ${name.trim()}.` });
      if (active?.localId === id) setActive((a) => (a ? { ...a, name: name.trim() } : a));
    }
    return outcome;
  }, [renameMyTemplate, active]);

  const handleDeleteMyTemplate = useCallback((id: string) => {
    const deleted = myTemplates.find((t) => t.id === id);
    removeMyTemplate(id);
    logApplyBrandUsage("apply-brand-template-deleted");
    if (deleted) setNotice({ tone: "success", text: `${deleted.name} deleted from Your Templates.` });
    deleteTemplateFile(id).catch((e) => logOfficeError(e, "apply-brand-delete-template-file"));
    const remembered = tabMemoryRef.current.saved?.active;
    if (remembered?.localId === id) delete tabMemoryRef.current.saved;
    if (active?.localId === id) {
      resetExtract();
      resetApply();
      setActive(null);
    }
  }, [myTemplates, removeMyTemplate, active, resetExtract, resetApply]);

  const handleDownloadMyTemplate = useCallback((template: MyBrandTemplate) => {
    getTemplateFile(template.id)
      .then((bytes) => {
        if (!bytes) {
          setNotice({ tone: "warning", text: `${template.name} can't be downloaded: its original file wasn't kept on this device.` });
          return;
        }
        downloadBytes(bytes, downloadFileName(template.name), PPTX_MIME_TYPE);
      })
      .catch((e) => logOfficeError(e, "apply-brand-download-template"));
  }, []);

  /** Saves a source to Your Templates, handling the 3-template limit (EC-22). */
  const saveToYourTemplates = useCallback((
    name: string, brand: BrandSpec, markKey: string, fileBytes: ArrayBuffer | null,
  ) => {
    const uniqueName = nextFreeNameForTemplates(name, myTemplates.map((t) => t.name));
    const { outcome, entry } = saveMyTemplate(uniqueName, brand, swatchesOf(brand));
    if (outcome === "limit") {
      setNotice({
        tone: "warning",
        text: `You have reached the limit of 3 saved templates. To save ${name}, delete one of your saved templates first.`,
        actions: [{ label: "Go to Your Templates", onClick: goToYourTemplates }],
      });
      return;
    }
    if (outcome !== "saved" || !entry) return;
    logApplyBrandUsage("apply-brand-template-saved");
    setSavedKeys((prev) => new Set(prev).add(markKey));
    setNotice({ tone: "success", text: `Saved. ${entry.name} is now listed under Templates · Your Templates.` });
    if (fileBytes) putTemplateFile(entry.id, fileBytes).catch((e) => logOfficeError(e, "apply-brand-store-template-file"));
  }, [myTemplates, saveMyTemplate, goToYourTemplates]);

  const saveMaster = useCallback(async (master: MasterInfo) => {
    const buffer = deckBufferRef.current;
    if (!buffer) return;
    const key = `theme:${master.id}`;
    const brand = edits[key]?.brand ?? (active?.key === key ? baseBrand : null)
      ?? await extractBrand(buffer, { themePath: master.themePath, masterPath: master.masterPath });
    saveToYourTemplates(`${master.name} theme`, brand, key, buffer);
  }, [edits, active, baseBrand, saveToYourTemplates]);

  const saveUpload = useCallback(async (upload: UploadEntry, theme: MasterInfo) => {
    const key = uploadKey(upload.id, theme.id);
    const cached = edits[`upload:${key}`]?.brand ?? brandCacheRef.current.get(key);
    const brand = cached ?? await extractBrand(upload.buffer, { themePath: theme.themePath, masterPath: theme.masterPath });
    brandCacheRef.current.set(key, brand);
    saveToYourTemplates(upload.themes.length > 1 ? theme.name : upload.name, brand, key, upload.buffer);
  }, [edits, saveToYourTemplates]);

  // ── Upload ─────────────────────────────────────────────────────────────────

  const readUpload = useCallback(async (file: File, mode: "add" | "replace" | "keep-both") => {
    lastFileRef.current = file;
    setProblem(null);
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setProblem({ tone: "error", title: "Connection lost.", text: "Nothing was uploaded. Check your connection, then try again.", isRetry: true });
      return;
    }
    setReading(file.name);
    try {
      const buffer = await file.arrayBuffer();
      if (looksPasswordProtected(buffer)) {
        setProblem({
          tone: "error", title: "This file can't be read.",
          text: "It's read-only or password-protected, so nothing was uploaded. Make it editable in PowerPoint, then try again.",
          isRetry: true,
        });
        return;
      }
      const masters = readMastersFromBuffer(buffer);
      if (masters === null) {
        setProblem({ tone: "error", title: "Couldn't upload the file.", text: "Something went wrong, so nothing was uploaded. Try again.", isRetry: true });
        return;
      }
      const themes = brandMasters(masters);
      if (themes.length === 0) {
        setProblem({
          tone: "warning", title: "This file doesn't have an active theme.",
          text: "It uses the default Office Theme, so nothing was uploaded. Choose a file with a brand theme instead.", isRetry: false,
        });
        return;
      }
      const first = themes[0]!;
      const brand = await extractBrand(buffer, { themePath: first.themePath, masterPath: first.masterPath });
      const taken = uploads.map((u) => u.name);
      let name = file.name;
      if (mode === "keep-both") name = nextFreeName(file.name, taken);
      const entry: UploadEntry = { id: newId(), name, buffer, themes, swatches: swatchesOf(brand) };
      const replaced = mode === "replace" ? uploads.find((u) => u.name === file.name) : undefined;
      if (replaced) {
        for (const t of replaced.themes) brandCacheRef.current.delete(uploadKey(replaced.id, t.id));
      }
      setUploads((prev) => (replaced ? prev.map((u) => (u.id === replaced.id ? entry : u)) : [...prev, entry]));
      brandCacheRef.current.set(uploadKey(entry.id, first.id), brand);
      clearFeedback();
      logApplyBrandUsage("apply-brand-upload-read");
      // FR-29 / EC-21: "<file> uploaded." on a fresh or Keep Both upload, "<name> replaced." on Replace.
      setNotice({ tone: "success", text: replaced ? `${name} replaced.` : `${name} uploaded.` });
      setActive({ tab: "upload", kind: "upload", key: uploadKey(entry.id, first.id), name: themes.length > 1 ? first.name : name });
      setFreshlyRead(uploadKey(entry.id, first.id));
      setDirectBrand(brand, name);
      if (mode === "keep-both") setRenameRequestId(entry.id);
    } catch (e) {
      logOfficeError(e, "apply-brand-upload");
      setProblem({ tone: "error", title: "Couldn't upload the file.", text: "Something went wrong, so nothing was uploaded. Try again.", isRetry: true });
    } finally {
      setReading(null);
    }
  }, [uploads, clearFeedback, setDirectBrand]);

  const handleFiles = useCallback((files: FileList | File[] | null) => {
    const file = files?.[0];
    // Closing the file dialog without choosing a file changes nothing (EC-20).
    if (!file) return;
    setProblem(null);
    if (!/\.(pptx|potx)$/i.test(file.name)) {
      setProblem({
        tone: "error", title: "That file type isn't supported.",
        text: `It's a ${fileTypeLabel(file.name)}, so nothing was uploaded. Choose a .pptx or .potx file instead.`, isRetry: false,
      });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      const mb = Number((file.size / (1024 * 1024)).toFixed(1));
      setProblem({
        tone: "error", title: "That file is too large.",
        text: `It's ${mb} MB and the limit is 50 MB, so nothing was uploaded. Remove large videos or images in PowerPoint, then try again.`,
        isRetry: false,
      });
      return;
    }
    if (uploads.some((u) => u.name === file.name)) {
      pendingDuplicateRef.current = file;
      setDuplicate({ name: file.name });
      return;
    }
    readUpload(file, "add").catch((e) => logOfficeError(e, "apply-brand-upload"));
  }, [uploads, readUpload]);

  const handleDuplicateChoice = useCallback((choice: "cancel" | "keep-both" | "replace") => {
    const file = pendingDuplicateRef.current;
    pendingDuplicateRef.current = null;
    setDuplicate(null);
    if (choice === "cancel" || !file) return;
    readUpload(file, choice).catch((e) => logOfficeError(e, "apply-brand-upload"));
  }, [readUpload]);

  const handleRemoveUpload = useCallback((upload: UploadEntry) => {
    const wasSaved = upload.themes.some((t) => savedKeys.has(uploadKey(upload.id, t.id)));
    setUploads((prev) => prev.filter((u) => u.id !== upload.id));
    for (const t of upload.themes) brandCacheRef.current.delete(uploadKey(upload.id, t.id));
    const remembered = tabMemoryRef.current.upload?.active;
    if (remembered?.key.startsWith(`upload:${upload.id}`) || remembered?.key.startsWith(`${upload.id}:`)) delete tabMemoryRef.current.upload;
    if (active?.kind === "upload" && active.key.startsWith(`${upload.id}:`)) {
      resetExtract();
      resetApply();
      setActive(null);
    }
    // No confirmation. A saved copy stays in Your Templates (BR-06).
    setNotice({
      tone: "info",
      text: wasSaved ? `${upload.name} removed from uploads. It's still in Your Templates.` : `${upload.name} removed from uploads.`,
    });
  }, [active, savedKeys, resetExtract, resetApply]);

  const handleRenameUpload = useCallback((id: string, name: string): "ok" | "duplicate" => {
    if (uploads.some((u) => u.id !== id && u.name.toLowerCase() === name.toLowerCase())) return "duplicate";
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, name } : u)));
    setActive((a) => (a?.kind === "upload" && a.key.startsWith(`${id}:`) && uploads.find((u) => u.id === id)?.themes.length === 1 ? { ...a, name } : a));
    return "ok";
  }, [uploads]);

  const customiseUpload = useCallback((upload: UploadEntry, theme: MasterInfo) => {
    selectUpload(upload, theme);
    setPendingCustomise({ key: uploadKey(upload.id, theme.id) });
  }, [selectUpload]);

  const customiseMaster = useCallback((master: MasterInfo) => {
    selectMaster(master, false);
    setPendingCustomise({ key: `theme:${master.id}` });
  }, [selectMaster]);

  // ── Apply ──────────────────────────────────────────────────────────────────

  const handleApply = useCallback(async () => {
    if (!finalBrand || !active) return;
    clearFeedback();
    // A brand template the organisation withdrew since it was picked can't be applied (BR-11, EC-04).
    // If the library can't be reached to check, the apply goes ahead as before.
    if (active.kind === "brand") {
      const stillListed = await fetchSavedBrandTemplates(http)
        .then((list) => list.some((t) => t.id === active.key.slice("org:".length)))
        .catch(() => true);
      if (!stillListed) {
        clearUnavailableSelection();
        setNotice({
          tone: "error",
          title: "That brand template is no longer available.",
          text: "Your organisation updated the library, so nothing was changed. Choose another one instead.",
        });
        return;
      }
    }
    const extras = {
      destination: scope === "deck" && saveTarget === "new" ? ("copy" as const) : ("update" as const),
      moveToMaster: scope === "deck" && removeOthers && active.kind === "theme" ? active.name : undefined,
    };
    apply(finalBrand, { scope, ...finalAdvanced }, extras).catch((e) => logOfficeError(e, "apply-brand-apply"));
  }, [finalBrand, active, scope, saveTarget, removeOthers, finalAdvanced, apply, clearFeedback, http, clearUnavailableSelection]);

  // After a successful apply: a brand template's Customise changes were for that apply only
  // (FR-65), and a whole-presentation update marks the source Applied (FR-08).
  const lastHandledResult = useRef<unknown>(null);
  useEffect(() => {
    if (applyState.status !== "done" || lastHandledResult.current === applyState.result || !active) return;
    lastHandledResult.current = applyState.result;
    logApplyBrandUsage(`apply-brand-applied-${scope}`);
    deckStaleRef.current = true;
    delete tabMemoryRef.current.active;
    if (sourceTab === "active") loadDeck().catch((e) => logOfficeError(e, "apply-brand-read-deck"));
    if (active.kind === "brand") {
      setEdits((prev) => {
        if (!(active.key in prev)) return prev;
        const next = { ...prev };
        delete next[active.key];
        return next;
      });
    }
    if (scope === "deck" && saveTarget === "current") setAppliedKey(active.key);
  }, [applyState, active, scope, saveTarget, sourceTab, loadDeck]);

  // Cancelled and failed applies are logged too (the failure itself is logged by the hook).
  useEffect(() => {
    if (applyState.status === "cancelled") logApplyBrandUsage("apply-brand-cancelled");
    else if (applyState.status === "error") logApplyBrandUsage(`apply-brand-failed-${applyState.kind}`);
  }, [applyState]);

  const handleScopeChange = useCallback((next: Scope) => {
    setScope(next);
    // Changing the scope resets the destination (FR-53); This Slide unticks Remove other masters (FR-25).
    setSaveTarget("current");
    if (next === "slide") setRemoveOthers(false);
  }, []);

  const isBusy = extractState.status === "extracting" || applyState.status === "applying" || reading !== null;
  const applyDisabled = !finalBrand || !onThisTab || isBusy;

  // ── Derived UI data ────────────────────────────────────────────────────────

  const sourceFonts = useMemo(
    () => (finalBrand ? findMissingFonts([finalBrand.fonts.titleFont, finalBrand.fonts.bodyFont]) : []),
    [finalBrand],
  );

  const addedForActive = active ? addedStyles[active.key] ?? [] : [];
  const allAdded = active !== null && active.kind !== "brand" && addedForActive.length > 0 && stillMissing.length === 0;

  const keptStylesText = useMemo(() => {
    if (!active || active.kind === "brand" || stillMissing.length === 0) return null;
    const list = listStyles(stillMissing);
    return `${list.charAt(0).toUpperCase()}${list.slice(1)} kept as they were. ${active.name} doesn't include them.`;
  }, [active, stillMissing]);

  const skippedActions = useMemo(() => {
    const lines: string[] = [];
    if (finalAdvanced.allowRoundedCorners) lines.push("Rounded corners kept as they were (Keep rounded corners is on)");
    if (finalAdvanced.allowShadows) lines.push("Shadows kept as they were (Keep shadows is on)");
    if (finalAdvanced.allowAnimations) lines.push("Animations kept as they were (Keep animations is on)");
    return lines;
  }, [finalAdvanced]);

  const renderNotes = useCallback((name: string, key: string) => {
    if (active?.key !== key || !baseBrand) return null;
    return (
      <>
        <SourceNotes
          sourceName={name}
          stillMissing={stillMissing}
          allAdded={allAdded}
          tableMissing={!(finalBrand?.tableDetected ?? true)}
          missingFonts={sourceFonts}
          showCaptured={freshlyRead === key}
          onAddMissing={openAddMissing}
        />
        <SelectedNote name={name} ready onReview={() => setCustomising({})} />
      </>
    );
  }, [active, baseBrand, stillMissing, allAdded, finalBrand, sourceFonts, freshlyRead, openAddMissing]);

  const savedStatus = savedTemplateStatus(active, extractState.status);
  const selection = selectionView(active, appliedKey, (key) => Boolean(edits[key]));
  const orgFontWarning = active?.kind === "brand" ? sourceFonts : [];

  // ── Render ─────────────────────────────────────────────────────────────────

  if (settingsOpen) return <SettingsScreen onBack={() => setSettingsOpen(false)} />;

  if (customising && baseBrand && active) {
    return (
      <CustomiseScreen
        brand={finalBrand ?? baseBrand}
        advanced={finalAdvanced}
        sourceKind={active.kind}
        sourceName={active.name}
        missingSections={customising.missing}
        onUseForThisApply={(b, adv, added) => finishCustomise(b, adv, added, "use")}
        onSaveChanges={(b, adv, added) => finishCustomise(b, adv, added, "save")}
        onDone={() => setCustomising(null)}
        onDiscard={() => {
          setCustomising(null);
          setNotice({
            tone: "info",
            text: active.kind === "yours" ? "Changes discarded. The saved template is unchanged." : "Changes discarded.",
          });
        }}
      />
    );
  }

  const introVisible = isIntroVisible(sourceTab, dismissedIntros, active, deck, uploads.length);

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader
          title="Apply Brand"
          subtitle="Deckorate"
          onBack={onBack}
          backLabel="Back"
          actions={
            <button
              type="button"
              className={styles.settingsBtn}
              aria-label="Apply Brand settings"
              title="Apply Brand settings"
              onClick={() => setSettingsOpen(true)}
            >
              <svg aria-hidden="true" viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="10" cy="10" r="2.5" />
                <path d="M10 3v2M10 15v2M3 10h2M15 10h2M5 5l1.4 1.4M13.6 13.6L15 15M5 15l1.4-1.4M13.6 6.4L15 5" />
              </svg>
            </button>
          }
        />

        {introVisible && (
          <output className={styles.introNote}>
            <span className={styles.introText}>
              <span className={styles.introTitle}>{INTRO_MESSAGE[sourceTab].title}</span>
              <span>{INTRO_MESSAGE[sourceTab].text}</span>
            </span>
            <button
              type="button"
              className={styles.introClose}
              aria-label="Close intro message"
              onClick={() => setDismissedIntros((prev) => new Set(prev).add(sourceTab))}
            >
              ✕
            </button>
          </output>
        )}

        <fieldset className={styles.sourceTabs} aria-label="Brand source">
          {([["saved", "Templates"], ["active", "This Deck’s Theme"], ["upload", "Upload"]] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={sourceTab === key}
              className={`${styles.sourceTabBtn} ${sourceTab === key ? styles.sourceTabBtnActive : ""}`}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          ))}
        </fieldset>

        {sourceTab === "saved" && (
          <SavedTemplatesTab
            selectedId={selection.savedId}
            disabled={isBusy}
            selectedStatus={savedStatus}
            customisedId={selection.customisedOrgId}
            appliedId={selection.appliedSavedId}
            isHighlightYours={highlightYours}
            onSelect={selectSavedTemplate}
            onSelectionUnavailable={clearUnavailableSelection}
            onCustomise={customiseSavedTemplate}
            myTemplates={myTemplates}
            onSelectMyTemplate={selectMyTemplate}
            onRenameMyTemplate={handleRenameMyTemplate}
            onDownloadMyTemplate={handleDownloadMyTemplate}
            onDeleteMyTemplate={handleDeleteMyTemplate}
          />
        )}

        {sourceTab === "saved" && active?.tab === "saved" && active.kind === "yours" && (
          <SourceNotes
            sourceName={active.name}
            stillMissing={stillMissing}
            allAdded={allAdded}
            tableMissing={!(finalBrand?.tableDetected ?? true)}
            missingFonts={sourceFonts}
            showCaptured={false}
            onAddMissing={openAddMissing}
          />
        )}

        {sourceTab === "active" && (
          <ThemeTab
            load={deck}
            selectedMasterId={active?.tab === "active" ? selectedMasterId : null}
            scope={scope}
            removeOthers={removeOthers}
            onRemoveOthersChange={setRemoveOthers}
            savedMasterIds={new Set([...savedKeys].filter((k) => k.startsWith("theme:")).map((k) => k.slice(6)))}
            appliedMasterId={selection.appliedMasterId}
            onSelectMaster={(m) => selectMaster(m)}
            onCustomise={customiseMaster}
            onSave={(m) => { saveMaster(m).catch((e) => logOfficeError(e, "apply-brand-save-theme")); }}
            onBrowseTemplates={() => handleTabChange("saved")}
            renderNotes={(m) => renderNotes(m.name, `theme:${m.id}`)}
            disabled={isBusy}
          />
        )}

        {sourceTab === "upload" && (
          <UploadTab
            uploads={uploads}
            selectedKey={selection.uploadKey}
            appliedKey={appliedKey}
            reading={reading}
            problem={problem}
            duplicate={duplicate}
            savedKeys={savedKeys}
            renameRequestId={renameRequestId}
            fileInputRef={fileInputRef}
            onFiles={handleFiles}
            onSelect={selectUpload}
            onCustomise={customiseUpload}
            onSave={(u, t) => { saveUpload(u, t).catch((e) => logOfficeError(e, "apply-brand-save-upload")); }}
            onRemove={handleRemoveUpload}
            onRename={handleRenameUpload}
            onRenameDone={() => setRenameRequestId(null)}
            onRetry={() => {
              const file = lastFileRef.current;
              if (file) readUpload(file, "add").catch((e) => logOfficeError(e, "apply-brand-upload"));
            }}
            onDismissProblem={() => setProblem(null)}
            onDuplicateChoice={handleDuplicateChoice}
            renderNotes={(u, t) => renderNotes(u.themes.length > 1 ? t.name : u.name, uploadKey(u.id, t.id))}
          />
        )}

        {extractState.status === "error" && sourceTab !== "saved" && (
          <BrandAlert tone="error" title="Extraction failed:">
            {extractState.message}
          </BrandAlert>
        )}


      <ApplyBar
        scope={scope}
        onScopeChange={handleScopeChange}
        saveTarget={saveTarget}
        onSaveTargetChange={setSaveTarget}
        sourceName={active?.name ?? ""}
        applyState={applyState}
        onCancel={cancelApply}
        onApply={() => { handleApply().catch((e) => logOfficeError(e, "apply-brand-apply")); }}
        disabled={applyDisabled}
        disabledReason={DISABLED_REASON[sourceTab]}
        notice={notice}
        onCloseNotice={() => setNotice(null)}
        keptStylesText={keptStylesText}
        skippedActions={skippedActions}
        slideCount={null}
        aboveButton={<OrgFontWarning fonts={orgFontWarning} />}
      />
      </div>
    </div>
  );
}

/** Same as nextFreeName but a clash only counts for a name without an extension clash on templates. */
function nextFreeNameForTemplates(name: string, taken: readonly string[]): string {
  return taken.some((t) => t.toLowerCase() === name.toLowerCase()) ? nextFreeName(name, taken) : name;
}

// ─── Alerts ────────────────────────────────────────────────────────────────

export type AlertTone = "error" | "warning" | "info" | "success";

interface BrandAlertProps {
  readonly tone: AlertTone;
  readonly title?: string;
  readonly children?: React.ReactNode;
  readonly actions?: readonly { label: string; onClick: () => void }[];
  /** Alerts in the apply bar and upload area can be closed with ✕ (BRD section 8). */
  readonly onClose?: () => void;
}

const ALERT_CLASS: Record<AlertTone, string> = {
  error: "brandAlertError", warning: "brandAlertWarning", info: "brandAlertInfo", success: "brandAlertSuccess",
};

const ICON: Record<AlertTone, string> = { error: "⛔", warning: "⚠", info: "ℹ", success: "✓" };

/** AiVA alert pattern (BRD section 8): status background, 4 px left border in
 *  the status colour, icon, title and body. Errors use role "alert"; warnings,
 *  info and success messages render as <output> (implicit role "status"). */
export function BrandAlert({ tone, title, children, actions = [], onClose }: Readonly<BrandAlertProps>) {
  const className = `${styles.brandAlert} ${styles[ALERT_CLASS[tone]]}`;
  const content = (
    <>
      <span className={styles.brandAlertIcon} aria-hidden="true">{ICON[tone]}</span>
      <div className={styles.brandAlertContent}>
        {title && <div className={styles.brandAlertTitle}>{title}</div>}
        {children && <div className={styles.brandAlertBody}>{children}</div>}
        {actions.length > 0 && (
          <div className={styles.brandAlertActions}>
            {actions.map((a) => (
              <button key={a.label} type="button" className={styles.brandAlertActionBtn} onClick={a.onClick}>
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {onClose && (
        <button type="button" className={styles.brandAlertClose} aria-label="Dismiss message" onClick={onClose}>
          ✕
        </button>
      )}
    </>
  );
  return tone === "error" ? (
    <div className={className} role="alert">{content}</div>
  ) : (
    <output className={className}>{content}</output>
  );
}

// ─── Source notes ──────────────────────────────────────────────────────────

const SECTION_LABEL: Record<StyleSection, string> = {
  title: "title", body: "body", subtitle: "subtitle", footnote: "footnote",
};

/** "title", "title and body", "title, body and footnote". */
export function listStyles(sections: readonly StyleSection[]): string {
  const labels = sections.map((s) => SECTION_LABEL[s]);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

const CAPTURED_VISIBLE_MS = 4000;

interface SourceNotesProps {
  /** Name shown in messages: the master, upload or template. */
  readonly sourceName: string;
  /** Sections still missing from the source (EC-09). */
  readonly stillMissing: readonly StyleSection[];
  /** True once every originally-missing section was added in Customise. */
  readonly allAdded: boolean;
  /** The source has no table style (EC-09). It can't be added: tables aren't customised. */
  readonly tableMissing: boolean;
  /** Fonts this source uses that aren't installed on the device (EC-11). */
  readonly missingFonts: readonly string[];
  /** Show the "captured" confirmation (FR-23) — only for a freshly read source. */
  readonly showCaptured: boolean;
  readonly onAddMissing: () => void;
}

/** Notes shown under a selected deck theme / upload / Your Template: the read
 *  confirmation (hides after 4 seconds), the missing-styles warning with Add
 *  Missing Styles, and the font-not-installed warning (BRD FR-23, EC-09, EC-11). */
export function SourceNotes({
  sourceName, stillMissing, allAdded, tableMissing, missingFonts, showCaptured, onAddMissing,
}: Readonly<SourceNotesProps>) {
  const [captured, setCaptured] = useState(showCaptured);

  useEffect(() => {
    if (!showCaptured) return;
    setCaptured(true);
    const id = setTimeout(() => setCaptured(false), CAPTURED_VISIBLE_MS);
    return () => clearTimeout(id);
  }, [showCaptured, sourceName]);

  const hasMissingWarning = stillMissing.length > 0 || tableMissing;

  return (
    <>
      {hasMissingWarning ? (
        <BrandAlert
          tone="warning"
          title={`Some styles are missing from ${sourceName}.`}
          actions={stillMissing.length > 0 ? [{ label: "Add Missing Styles", onClick: onAddMissing }] : []}
        >
          {stillMissing.length > 0 && <>No {listStyles(stillMissing)} styles found. Add them, or slides keep their current look.</>}
          {tableMissing && <> Table style isn&rsquo;t included. Tables keep their current look.</>}
        </BrandAlert>
      ) : (
        <>
          {allAdded && <BrandAlert tone="success">Missing styles added in Customise</BrandAlert>}
          {!allAdded && captured && <BrandAlert tone="success">Colours, fonts and styles captured</BrandAlert>}
        </>
      )}
      {missingFonts.length > 0 && (
        <BrandAlert tone="warning">
          <b>{formatFontList(missingFonts)}</b> {missingFonts.length > 1 ? "aren't" : "isn't"} installed on this device.{" "}
          {sourceName} uses {missingFonts.length > 1 ? "these fonts" : "this font"}. PowerPoint will show a substitute until
          it&rsquo;s installed, or choose another font in Customise.
        </BrandAlert>
      )}
    </>
  );
}

// ─── Colour picker ─────────────────────────────────────────────────────────

interface EyeDropperLike { open: (opts?: { signal?: AbortSignal }) => Promise<{ sRGBHex: string }> }
type EyeDropperCtor = new () => EyeDropperLike;

function getEyeDropper(): EyeDropperCtor | null {
  const ctor = (globalThis as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  return ctor ?? null;
}

/** True when the host can sample a colour from the screen (BRD NFR-07: only
 *  offered wherever the eyedropper is supported). */
export function eyedropperSupported(): boolean {
  return getEyeDropper() !== null;
}

/** Runs the screen eyedropper, resolving to a 6-digit hex or null when
 *  cancelled (Esc) or unsupported. */
export async function pickColourFromScreen(): Promise<string | null> {
  const Ctor = getEyeDropper();
  if (!Ctor) return null;
  try {
    const result = await new Ctor().open();
    return toHex6(result.sRGBHex);
  } catch (error) {
    // Pressing Esc rejects with an AbortError: an expected cancel, not a failure.
    if (!(error instanceof DOMException && error.name === "AbortError")) logOfficeError(error, "apply-brand-eyedropper");
    return null;
  }
}

export type PickerMode = "edit" | "add" | "existing-secondary";

interface ColourPickerProps {
  readonly title: string;
  readonly value: string;
  readonly mode?: PickerMode;
  readonly onApply: (hex: string) => void;
  readonly onCancel: () => void;
  readonly onRemove?: () => void;
}

type Texts = Record<ColourField, string>;

function textsFromHex(hex: string): Texts {
  const rgb = parseHexRgb(hex);
  const hsl = rgbToHslParts(rgb);
  return {
    hex: `#${hex}`,
    r: String(rgb.r), g: String(rgb.g), b: String(rgb.b),
    h: String(Math.round(hsl.h)), s: String(Math.round(hsl.s * 100)), l: String(Math.round(hsl.l * 100)),
  };
}

/** The custom colour picker (BRD FR-41): saturation/brightness area, hue
 *  slider, eyedropper, preview dot and RGB / HSL / HEX fields, with Cancel and
 *  Apply (Add for a new secondary colour, Remove for an existing one). Apply is
 *  blocked while any field holds an invalid value (EC-26). */
export function ColourPicker({ title, value, mode = "edit", onApply, onCancel, onRemove }: Readonly<ColourPickerProps>) {
  const initial = isValidHex(value) ? toHex6(value) : "808080";
  const [hsv, setHsv] = useState(() => rgbToHsv(parseHexRgb(initial)));
  const hex = formatRgbHex(hsvToRgb(hsv));
  const [texts, setTexts] = useState<Texts>(() => textsFromHex(initial));
  const [errors, setErrors] = useState<Partial<Record<ColourField, string>>>({});
  const [picking, setPicking] = useState(false);
  const cardRef = useRef<HTMLDialogElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const saturationRef = useRef<HTMLInputElement>(null);
  const firstFieldRef = useRef<HTMLButtonElement>(null);

  const setFromHsv = useCallback((next: typeof hsv) => {
    setHsv(next);
    setTexts(textsFromHex(formatRgbHex(hsvToRgb(next))));
    setErrors({});
  }, []);

  useEffect(() => {
    (firstFieldRef.current ?? saturationRef.current)?.focus();
  }, []);

  // Escape closes the picker without letting the key reach the panel. A native
  // listener keeps keyboard handlers off the non-interactive <dialog> element.
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    };
    card.addEventListener("keydown", onKeyDown);
    return () => card.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const moveInArea = (clientX: number, clientY: number) => {
    const rect = areaRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    const s = clampNumber((clientX - rect.left) / rect.width, 0, 1);
    const v = clampNumber(1 - (clientY - rect.top) / rect.height, 0, 1);
    setFromHsv({ ...hsv, s, v });
  };

  const onFieldChange = (field: ColourField, raw: string) => {
    setTexts((t) => ({ ...t, [field]: raw }));
    const error = validateColourField(field, raw);
    setErrors((e) => ({ ...e, [field]: error ?? undefined }));
    if (error) return;
    const t = { ...texts, [field]: raw };
    if (field === "hex") {
      setHsv(rgbToHsv(parseHexRgb(toHex6(raw))));
      setTexts(textsFromHex(toHex6(raw)));
    } else if (field === "r" || field === "g" || field === "b") {
      const rgb = { r: Number(t.r), g: Number(t.g), b: Number(t.b) };
      setHsv(rgbToHsv(rgb));
      setTexts({ ...textsFromHex(formatRgbHex(rgb)), [field]: raw });
    } else {
      const rgb = hslPartsToRgb({ h: Number(t.h), s: Number(t.s) / 100, l: Number(t.l) / 100 });
      setHsv(rgbToHsv(rgb));
      setTexts({ ...textsFromHex(formatRgbHex(rgb)), [field]: raw });
    }
  };

  // Leaving an invalid field restores the last valid value (EC-26).
  const onFieldBlur = (field: ColourField) => {
    if (!validateColourField(field, texts[field])) return;
    setTexts(textsFromHex(hex));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const hasError = Object.values(errors).some(Boolean);

  const pick = async () => {
    setPicking(true);
    const picked = await pickColourFromScreen();
    setPicking(false);
    if (picked) setFromHsv(rgbToHsv(parseHexRgb(picked)));
  };

  const applyLabel = mode === "add" ? "Add" : "Apply";
  const fields: Array<{ group: string; items: Array<{ field: ColourField; label: string }> }> = [
    { group: "RGB", items: [{ field: "r", label: "R" }, { field: "g", label: "G" }, { field: "b", label: "B" }] },
    { group: "HSL", items: [{ field: "h", label: "H" }, { field: "s", label: "S" }, { field: "l", label: "L" }] },
  ];
  const firstError = (Object.values(errors).find(Boolean) as string | undefined) ?? null;

  return (
    <dialog ref={cardRef} open className={styles.czPickerCard} aria-label={title}>
      <div className={styles.czPickerTitle}>{title}</div>
      {/* The 2D area is pointer-only; keyboard and screen-reader users drive the
          same values through the two visually hidden native range inputs. */}
      <div
        ref={areaRef}
        className={styles.czPickerArea}
        style={{ backgroundColor: `#${formatRgbHex(hsvToRgb({ h: hsv.h, s: 1, v: 1 }))}` }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture?.(e.pointerId);
          moveInArea(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) moveInArea(e.clientX, e.clientY);
        }}
      >
        <div className={styles.czPickerAreaWhite} aria-hidden="true" />
        <div className={styles.czPickerAreaBlack} aria-hidden="true" />
        <span
          className={styles.czPickerThumb}
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
          aria-hidden="true"
        />
        <input
          ref={saturationRef}
          type="range"
          className={styles.srOnly}
          min={0}
          max={100}
          value={Math.round(hsv.s * 100)}
          aria-label="Saturation"
          aria-valuetext={`${Math.round(hsv.s * 100)}%`}
          onChange={(e) => setFromHsv({ ...hsv, s: Number(e.target.value) / 100 })}
        />
        <input
          type="range"
          className={styles.srOnly}
          min={0}
          max={100}
          value={Math.round(hsv.v * 100)}
          aria-label="Brightness"
          aria-valuetext={`${Math.round(hsv.v * 100)}%`}
          onChange={(e) => setFromHsv({ ...hsv, v: Number(e.target.value) / 100 })}
        />
      </div>

      <div className={styles.czPickerRow}>
        {/* Decorative: the same colour is announced through the HEX field below. */}
        <span className={styles.czPickerDot} style={{ backgroundColor: `#${hex}` }} aria-hidden="true" />
        <input
          type="range"
          className={styles.czPickerHue}
          min={0}
          max={360}
          value={Math.round(hsv.h)}
          aria-label="Hue"
          onChange={(e) => setFromHsv({ ...hsv, h: Number(e.target.value) })}
        />
        {eyedropperSupported() && (
          <button ref={firstFieldRef} type="button" className={styles.czSmallBtn} onClick={pick} aria-label="Eyedropper" title="Eyedropper">
            ⌖
          </button>
        )}
      </div>
      {picking && <output className={styles.czPickerHint}>Click anywhere to pick a colour. Press Esc to cancel.</output>}

      <label className={styles.czPickerFieldRow}>
        <span>HEX</span>
        <input
          type="text"
          className={styles.czPickerInput}
          value={texts.hex}
          aria-label="HEX"
          aria-invalid={Boolean(errors.hex)}
          onChange={(e) => onFieldChange("hex", e.target.value)}
          onBlur={() => onFieldBlur("hex")}
        />
      </label>
      {fields.map(({ group, items }) => (
        <div key={group} className={styles.czPickerFieldRow}>
          <span>{group}</span>
          {items.map(({ field, label }) => (
            <input
              key={field}
              type="text"
              inputMode="numeric"
              className={styles.czPickerInputSmall}
              value={texts[field]}
              aria-label={`${group} ${label}`}
              aria-invalid={Boolean(errors[field])}
              onChange={(e) => onFieldChange(field, e.target.value)}
              onBlur={() => onFieldBlur(field)}
            />
          ))}
        </div>
      ))}
      {firstError && <div className={styles.czFieldError} role="alert">{firstError}</div>}

      <div className={styles.czPickerActions}>
        {mode === "existing-secondary" && onRemove && (
          <button type="button" className={styles.czSmallBtn} onClick={onRemove}>Remove</button>
        )}
        <button type="button" className={styles.czSmallBtn} onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className={styles.czPrimarySmallBtn}
          disabled={hasError}
          aria-disabled={hasError}
          onClick={() => onApply(hex)}
        >
          {applyLabel}
        </button>
      </div>
    </dialog>
  );
}

// ─── Font combobox ─────────────────────────────────────────────────────────

interface FontComboboxProps {
  readonly label: string;
  readonly value: string;
  readonly fonts: readonly FontOption[];
  /** The brand's theme fonts, offered as the "Theme Fonts" group. */
  readonly themeFonts: readonly { label: string; name: string }[];
  readonly onChange: (name: string) => void;
}

interface Row {
  readonly id: string;
  readonly name: string;
  readonly hint?: string;
}

/** Heading / Body font field (BRD FR-43): type-to-filter combobox with a
 *  Theme Fonts group and an All Fonts group, each font shown in its own
 *  typeface and the current one ticked. Arrow keys, Enter, Esc and Tab are
 *  supported. When nothing matches, "Use “<text>” anyway" accepts the typed
 *  name; clearing the field restores the previous font. */
export function FontCombobox({ label, value, fonts, themeFonts, onChange }: Readonly<FontComboboxProps>) {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const query = typed ? text.trim().toLowerCase() : "";
  const themeRows: Row[] = useMemo(
    () =>
      themeFonts
        .filter((t) => t.name && (!query || t.name.toLowerCase().includes(query)))
        .map((t) => ({ id: `${baseId}-theme-${t.label}`, name: t.name, hint: t.label })),
    [themeFonts, query, baseId],
  );
  const allRows: Row[] = useMemo(
    () =>
      fonts
        .filter((f) => !query || f.name.toLowerCase().includes(query))
        .map((f, i) => ({ id: `${baseId}-all-${i}`, name: f.name })),
    [fonts, query, baseId],
  );
  const rows = [...themeRows, ...allRows];
  const noMatch = query !== "" && rows.length === 0;

  function close() {
    setOpen(false);
    setTyped(false);
    // Closing without choosing (including after clearing the field) restores the current font.
    setText(value);
  }

  const choose = (name: string) => {
    setText(name);
    setOpen(false);
    setTyped(false);
    if (name !== value) onChange(name);
    inputRef.current?.focus();
  };

  const moveActive = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!open) {
      setOpen(true);
      return;
    }
    if (rows.length === 0) return;
    const step = e.key === "ArrowDown" ? 1 : -1;
    setActive((a) => (a + step + rows.length) % rows.length);
  };

  const chooseActive = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    e.preventDefault();
    const row = rows[active];
    if (row) choose(row.name);
    else if (noMatch) choose(text.trim());
  };

  const dismiss = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    e.preventDefault();
    e.stopPropagation();
    close();
  };

  const keyHandlers: Partial<Record<string, (e: React.KeyboardEvent<HTMLInputElement>) => void>> = {
    ArrowDown: moveActive,
    ArrowUp: moveActive,
    Enter: chooseActive,
    Escape: dismiss,
    Tab: () => {
      if (open) close();
    },
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => keyHandlers[e.key]?.(e);

  const activeRow = rows[active];
  return (
    <div className={styles.czFieldRow} ref={wrapRef}>
      <label htmlFor={`${baseId}-input`} className={styles.czFieldLabel}>{label}</label>
      <div className={styles.czComboWrap}>
        <input
          ref={inputRef}
          id={`${baseId}-input`}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && activeRow ? activeRow.id : undefined}
          className={styles.czComboInput}
          style={{ fontFamily: `"${value}", sans-serif` }}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setTyped(true);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {open && (
          // ARIA editable-combobox pattern: a native <select>/<datalist> can't filter as you type or preview fonts.
          <div id={listId} role="listbox" aria-label={`${label} fonts`} className={styles.czComboList} /* NOSONAR */>
            {noMatch ? (
              <div className={styles.czComboEmpty}>
                <div>No fonts match &ldquo;{text.trim()}&rdquo;</div>
                <button type="button" className={styles.czLinkBtn} onClick={() => choose(text.trim())}>
                  Use &ldquo;{text.trim()}&rdquo; anyway
                </button>
              </div>
            ) : (
              <>
                {themeRows.length > 0 && <div className={styles.czComboGroup}>Theme Fonts</div>}
                {themeRows.map((row) => (
                  <FontRow key={row.id} row={row} rowIndex={rows.indexOf(row)} active={rows[active] === row} current={row.name === value} onChoose={choose} />
                ))}
                {allRows.length > 0 && <div className={styles.czComboGroup}>All Fonts</div>}
                {allRows.map((row) => (
                  <FontRow key={row.id} row={row} rowIndex={rows.indexOf(row)} active={rows[active] === row} current={row.name === value} onChoose={choose} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FontRow({ row, rowIndex, active, current, onChoose }: Readonly<{
  row: Row; rowIndex: number; active: boolean; current: boolean; onChoose: (name: string) => void;
}>) {
  return (
    // Focus stays in the combobox input, which points here via aria-activedescendant.
    <div // NOSONAR
      id={row.id}
      role="option"
      aria-selected={current}
      data-index={rowIndex}
      className={`${styles.czComboOption} ${active ? styles.czComboOptionActive : ""}`}
      style={{ fontFamily: `"${row.name}", sans-serif` }}
      onMouseDown={(e) => {
        e.preventDefault();
        onChoose(row.name);
      }}
    >
      <span className={styles.czComboTick} aria-hidden="true">{current ? "✓" : ""}</span>
      <span className={styles.czComboName}>{row.name}</span>
      {row.hint && <span className={styles.czComboHint}>{row.hint}</span>}
    </div>
  );
}

// ─── Font colour gallery ───────────────────────────────────────────────────

const STANDARD_COLOURS = [
  "C00000", "FF0000", "FFC000", "FFFF00", "92D050", "00B050", "00B0F0", "0070C0", "002060", "7030A0",
];

interface FontColourGalleryProps {
  readonly colors: BrandColors;
  readonly secondary: readonly string[];
  readonly value: string;
  readonly onPick: (hex: string) => void;
  readonly onEyedropper: () => void;
  readonly onMoreColours: () => void;
}

/** Font colour gallery (BRD FR-46): Theme Colours (10 swatches plus 5 rows of
 *  lighter and darker shades), Custom Colours (the brand's secondary colours),
 *  Standard Colours (10), Eyedropper and More Colours…. */
export function FontColourGallery({ colors, secondary, value, onPick, onEyedropper, onMoreColours }: Readonly<FontColourGalleryProps>) {
  const theme: ThemeColors = {
    dk1: colors.dk1, lt1: colors.lt1, dk2: colors.dk2, lt2: colors.lt2,
    accent1: colors.accent1, accent2: colors.accent2, accent3: colors.accent3,
    accent4: colors.accent4, accent5: colors.accent5, accent6: colors.accent6,
    hlink: colors.hlink ?? "0563C1", folHlink: colors.folHlink ?? "954F72",
  };
  const grid = buildPaletteGrid(theme);
  const current = value.toUpperCase();

  const swatch = (hex: string, label: string) => (
    <button
      key={`${label}-${hex}`}
      type="button"
      className={`${styles.czGallerySwatch} ${hex.toUpperCase() === current ? styles.czGallerySwatchOn : ""}`}
      style={{ backgroundColor: `#${hex}` }}
      title={`${label} #${hex}`}
      aria-label={`${label} #${hex}`}
      aria-pressed={hex.toUpperCase() === current}
      onClick={() => onPick(hex.toUpperCase())}
    />
  );

  return (
    <fieldset className={styles.czGallery} aria-label="Font colour">
      <div className={styles.czGalleryLabel}>Theme Colours</div>
      <div className={styles.czGalleryGrid}>
        {/* The first row of the grid is the 10 base swatches; the rest are shades. */}
        {grid.flatMap((row, ri) => row.map((cell) => swatch(cell.hex, ri === 0 ? cell.key : cell.label)))}
      </div>
      {secondary.length > 0 && (
        <>
          <div className={styles.czGalleryLabel}>Custom Colours</div>
          <div className={styles.czGalleryRow}>{secondary.map((hex) => swatch(hex, "Custom"))}</div>
        </>
      )}
      <div className={styles.czGalleryLabel}>Standard Colours</div>
      <div className={styles.czGalleryRow}>{STANDARD_COLOURS.map((hex) => swatch(hex, "Standard"))}</div>
      <div className={styles.czGalleryActions}>
        {eyedropperSupported() && (
          <button type="button" className={styles.czLinkBtn} onClick={onEyedropper}>
            Eyedropper
          </button>
        )}
        <button type="button" className={styles.czLinkBtn} onClick={onMoreColours}>
          More Colours…
        </button>
      </div>
    </fieldset>
  );
}

// ─── Customise screen ──────────────────────────────────────────────────────

/** Which kind of source is being customised — drives the info message and footer (BRD FR-37, FR-48). */
export type CustomiseSourceKind = "brand" | "yours" | "theme" | "upload";

/** The text-format sections a source can be missing (BRD EC-09). */
export type StyleSection = "title" | "body" | "subtitle" | "footnote";

export interface CustomiseAdvanced {
  allowShadows: boolean;
  isAllowGradients: boolean;
  allowRoundedCorners: boolean;
  allowAnimations: boolean;
  compressImages: boolean;
  isFixAlignment: boolean;
  autofitSlideContent: boolean;
}

interface CustomiseScreenProps {
  readonly brand: BrandSpec;
  readonly advanced: CustomiseAdvanced;
  readonly sourceKind: CustomiseSourceKind;
  readonly sourceName: string;
  /** Sections the source doesn't define — expanded and marked NOT SET (FR-38). */
  readonly missingSections?: readonly StyleSection[];
  /** Brand template: keep the changes for this apply only (FR-49). */
  readonly onUseForThisApply: (brand: BrandSpec, advanced: CustomiseAdvanced, added: StyleSection[]) => void;
  /** Deck theme / upload / Your Template: save, overwriting the previous version (FR-50). */
  readonly onSaveChanges: (brand: BrandSpec, advanced: CustomiseAdvanced, added: StyleSection[]) => void;
  /** No changes were made (Done, or Back with nothing to lose). */
  readonly onDone: () => void;
  /** Discards the changes (Cancel, or Discard in the leave dialog) (FR-51). */
  readonly onDiscard: () => void;
}

const SECTION_ORDER = ["colours", "fonts", "title", "body", "subtitle", "footnote", "advanced"] as const;
type SectionKey = (typeof SECTION_ORDER)[number];

const SECTION_TITLES: Record<SectionKey, string> = {
  colours: "Colours",
  fonts: "Fonts",
  title: "Title Format",
  body: "Body Text Format",
  subtitle: "Subtitle Format",
  footnote: "Footnote Format",
  advanced: "Advanced Options",
};

/** Each section's open state is remembered for the rest of the session (FR-38). */
const sectionOpenMemory: Partial<Record<SectionKey, boolean>> = { colours: true };

const THEME_SLOTS: Array<{ key: keyof BrandSpec["colors"]; label: string }> = [
  { key: "dk1", label: "Dark 1" }, { key: "lt1", label: "Light 1" },
  { key: "dk2", label: "Dark 2" }, { key: "lt2", label: "Light 2" },
  { key: "accent1", label: "Accent 1" }, { key: "accent2", label: "Accent 2" },
  { key: "accent3", label: "Accent 3" }, { key: "accent4", label: "Accent 4" },
  { key: "accent5", label: "Accent 5" }, { key: "accent6", label: "Accent 6" },
  { key: "hlink", label: "Hyperlink" }, { key: "folHlink", label: "Followed Hyperlink" },
];

const INFO_MESSAGE: Record<CustomiseSourceKind, (name: string) => string> = {
  brand: () => "Changes are for this apply only. Brand templates are managed by your organisation, so your changes can't be saved to the template.",
  yours: (name) => `Changes update this template. Saving updates ${name} in Your Templates, so your changes are used every time you apply it.`,
  theme: () => "Keep your changes. Save this as a template to reuse your changes later.",
  upload: () => "Keep your changes. Save this as a template to reuse your changes later.",
};

const STANDARD_SIZES = [8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 44, 48, 54, 60, 66, 72, 80, 88, 96];
const MIN_SIZE = 1;
const MAX_SIZE = 400;

/** Next size up/down through the standard sizes 8 to 96, then in steps of 8 (FR-45). */
export function stepSize(current: number, direction: 1 | -1): number {
  let next: number;
  if (direction === 1) {
    next = STANDARD_SIZES.find((s) => s > current) ?? (Math.floor(current / 8) + 1) * 8;
  } else if (current > 96) {
    next = Math.max(96, (Math.ceil(current / 8) - 1) * 8);
  } else {
    next = [...STANDARD_SIZES].reverse().find((s) => s < current) ?? STANDARD_SIZES[0]!;
  }
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, next));
}

/** Parses a typed size; returns the EC-24 message when it isn't valid. */
export function validateSize(raw: string): { value: number } | { error: string } {
  const text = raw.trim();
  if (text === "" || Number.isNaN(Number(text))) return { error: "Enter a number, like 12." };
  const n = Number(text);
  if (n < MIN_SIZE || n > MAX_SIZE) return { error: "Enter a size from 1 to 400." };
  return { value: n };
}

type FormatKey = "titleFormat" | "bodyTextFormat" | "subtitleFormat" | "footnoteFormat";
const FORMAT_KEY: Record<StyleSection, FormatKey> = {
  title: "titleFormat", body: "bodyTextFormat", subtitle: "subtitleFormat", footnote: "footnoteFormat",
};

export function CustomiseScreen({
  brand, advanced, sourceKind, sourceName, missingSections = [], onUseForThisApply, onSaveChanges, onDone, onDiscard,
}: Readonly<CustomiseScreenProps>) {
  const [draft, setDraft] = useState<BrandSpec>(brand);
  const [draftAdv, setDraftAdv] = useState<CustomiseAdvanced>(advanced);
  const [fonts, setFonts] = useState<FontOption[]>([]);
  const [touched, setTouched] = useState<ReadonlySet<StyleSection>>(new Set());
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [open, setOpen] = useState<Partial<Record<SectionKey, boolean>>>(() => {
    const next: Partial<Record<SectionKey, boolean>> = { ...sectionOpenMemory };
    for (const s of missingSections) next[s] = true;
    return next;
  });
  const firstMissingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    loadFontData().then((data) => { if (live) setFonts(data.allFonts); }).catch(() => undefined);
    return () => { live = false; };
  }, []);

  // Opened from Add Missing Styles: the first missing section is brought into view.
  useEffect(() => {
    if (missingSections.length > 0) firstMissingRef.current?.scrollIntoView?.({ block: "start" });
  }, []);

  const unsetSections = useMemo(
    () => new Set(missingSections.filter((s) => !touched.has(s))),
    [missingSections, touched],
  );

  const dirty =
    touched.size > 0 || JSON.stringify(draft) !== JSON.stringify(brand) || JSON.stringify(draftAdv) !== JSON.stringify(advanced);

  const toggle = (key: SectionKey) => {
    setOpen((o) => {
      const next = !o[key];
      sectionOpenMemory[key] = next;
      return { ...o, [key]: next };
    });
  };

  const markTouched = useCallback((section: StyleSection) => {
    setTouched((t) => (t.has(section) ? t : new Set(t).add(section)));
  }, []);

  const secondary = draft.secondaryColors ?? [];
  const [picker, setPicker] = useState<
    | { kind: "slot"; key: keyof BrandSpec["colors"]; label: string }
    | { kind: "secondary-add" }
    | { kind: "secondary-edit"; hex: string }
    | { kind: "format"; section: StyleSection }
    | { kind: "highlight" }
    | null
  >(null);
  const [gallery, setGallery] = useState<StyleSection | null>(null);
  // The font colour gallery's own Eyedropper button (FR-42) — shows the same
  // hint as the custom picker's while a pick is in progress.
  const [galleryPicking, setGalleryPicking] = useState(false);

  const setSlot = (key: keyof BrandSpec["colors"], hex: string) => {
    setDraft((d) => ({ ...d, colors: { ...d.colors, [key]: hex } }));
  };
  const addSecondary = (hex: string) => {
    // A colour already in the list is not added again (FR-40).
    setDraft((d) => {
      const list = d.secondaryColors ?? [];
      return list.includes(hex) ? d : { ...d, secondaryColors: [...list, hex] };
    });
  };
  const removeSecondary = (hex: string) => {
    setDraft((d) => ({ ...d, secondaryColors: (d.secondaryColors ?? []).filter((c) => c !== hex) }));
  };

  const setFormat = (section: StyleSection, patch: Partial<PlaceholderFormat>) => {
    markTouched(section);
    const key = FORMAT_KEY[section];
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  };
  const setHeadingFont = (name: string) => {
    markTouched("title");
    setDraft((d) => ({ ...d, fonts: { ...d.fonts, titleFont: name }, titleFormat: { ...d.titleFormat, fontName: name } }));
  };
  // The Body font cascades to subtitle/footnote too — brandApplier reads each
  // format's own fontName independently, and Customise exposes one Body control.
  const setBodyFont = (name: string) => {
    setDraft((d) => ({
      ...d,
      fonts: { ...d.fonts, bodyFont: name },
      bodyTextFormat: { ...d.bodyTextFormat, fontName: name },
      subtitleFormat: { ...d.subtitleFormat, fontName: name },
      footnoteFormat: { ...d.footnoteFormat, fontName: name },
    }));
  };

  const missingFonts = useMemo(
    () => findMissingFonts([draft.fonts.titleFont, draft.fonts.bodyFont]),
    [draft.fonts.titleFont, draft.fonts.bodyFont],
  );

  const handleBack = () => {
    if (dirty) setLeaveOpen(true);
    else onDone();
  };

  const added = [...touched];
  const fontSummary =
    draft.fonts.titleFont === draft.fonts.bodyFont ? draft.fonts.titleFont : `${draft.fonts.titleFont} · ${draft.fonts.bodyFont}`;

  const formatSummary = (section: StyleSection) => {
    if (unsetSections.has(section)) return "–";
    const f = draft[FORMAT_KEY[section]];
    const flags = [f.bold && "Bold", f.isItalic && "Italic", f.isUnderline && "Underline"].filter(Boolean).join(", ");
    const base = `${f.fontSize} pt · #${f.fontColor.toUpperCase()}`;
    return flags ? `${base} · ${flags}` : base;
  };

  const summaries: Record<SectionKey, string> = {
    colours: `${secondary.length} secondary`,
    fonts: fontSummary,
    title: formatSummary("title"),
    body: formatSummary("body"),
    subtitle: formatSummary("subtitle"),
    footnote: formatSummary("footnote"),
    advanced: "",
  };

  const renderFormat = (section: StyleSection, withStyleToggles: boolean) => {
    const key = FORMAT_KEY[section];
    const f = draft[key];
    const unset = unsetSections.has(section);
    return (
      <div className={styles.czSectionBody}>
        <SizeField
          label="Size"
          value={unset ? null : f.fontSize}
          onChange={(v) => setFormat(section, { fontSize: v })}
        />
        {withStyleToggles && (
          <div className={styles.czStyleToggles}>
            <label className={styles.czCheckRow}>
              <input type="checkbox" checked={f.bold ?? false} onChange={(e) => setFormat(section, { bold: e.target.checked })} />
              <span>Bold</span>
            </label>
            <label className={styles.czCheckRow}>
              <input type="checkbox" checked={f.isItalic ?? false} onChange={(e) => setFormat(section, { isItalic: e.target.checked })} />
              <span>Italic</span>
            </label>
            <label className={styles.czCheckRow}>
              <input type="checkbox" checked={f.isUnderline ?? false} onChange={(e) => setFormat(section, { isUnderline: e.target.checked })} />
              <span>Underline</span>
            </label>
          </div>
        )}
        <div className={styles.czFieldRow}>
          <span className={styles.czFieldLabel}>Colour</span>
          <button
            type="button"
            className={styles.czColourButton}
            aria-label={`${SECTION_TITLES[section]} colour`}
            aria-expanded={gallery === section}
            onClick={() => setGallery((g) => (g === section ? null : section))}
          >
            {unset ? (
              <span>–</span>
            ) : (
              <>
                <span className={styles.czColourDot} style={{ backgroundColor: `#${f.fontColor}` }} />
                <span>#{f.fontColor.toUpperCase()}</span>
              </>
            )}
          </button>
        </div>
        {gallery === section && (
          <>
            <FontColourGallery
              colors={draft.colors}
              secondary={secondary}
              value={f.fontColor}
              onPick={(hex) => {
                setFormat(section, { fontColor: hex });
                setGallery(null);
              }}
              onEyedropper={() => {
                setGalleryPicking(true);
                pickColourFromScreen().then((hex) => {
                  setGalleryPicking(false);
                  if (hex) setFormat(section, { fontColor: hex });
                  setGallery(null);
                }).catch(() => { setGalleryPicking(false); setGallery(null); });
              }}
              onMoreColours={() => {
                setGallery(null);
                setPicker({ kind: "format", section });
              }}
            />
            {galleryPicking && <output className={styles.czPickerHint}>Click anywhere to pick a colour. Press Esc to cancel.</output>}
          </>
        )}
        {section === "title" && (
          <div className={styles.czFieldRow}>
            <span className={styles.czFieldLabel}>Highlight</span>
            <button type="button" className={styles.czColourButton} aria-label="Highlight colour" onClick={() => setPicker({ kind: "highlight" })}>
              <span className={styles.czColourDot} style={{ backgroundColor: `#${draft.highlightColor}` }} />
              <span>#{draft.highlightColor.toUpperCase()}</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const pickerNode = (() => {
    if (!picker) return null;
    const close = () => setPicker(null);
    if (picker.kind === "slot") {
      return (
        <ColourPicker
          title={picker.label}
          value={draft.colors[picker.key] ?? "808080"}
          onCancel={close}
          onApply={(hex) => { setSlot(picker.key, hex); close(); }}
        />
      );
    }
    if (picker.kind === "secondary-add") {
      return <ColourPicker title="Add secondary colour" value="0B5DAA" mode="add" onCancel={close} onApply={(hex) => { addSecondary(hex); close(); }} />;
    }
    if (picker.kind === "secondary-edit") {
      const mode: PickerMode = "existing-secondary";
      return (
        <ColourPicker
          title="Secondary colour"
          value={picker.hex}
          mode={mode}
          onCancel={close}
          onRemove={() => { removeSecondary(picker.hex); close(); }}
          onApply={(hex) => {
            setDraft((d) => ({ ...d, secondaryColors: (d.secondaryColors ?? []).map((c) => (c === picker.hex ? hex : c)) }));
            close();
          }}
        />
      );
    }
    if (picker.kind === "highlight") {
      return (
        <ColourPicker
          title="Highlight colour"
          value={draft.highlightColor}
          onCancel={close}
          onApply={(hex) => { markTouched("title"); setDraft((d) => ({ ...d, highlightColor: hex })); close(); }}
        />
      );
    }
    return (
      <ColourPicker
        title="More colours"
        value={draft[FORMAT_KEY[picker.section]].fontColor}
        onCancel={close}
        onApply={(hex) => { setFormat(picker.section, { fontColor: hex }); close(); }}
      />
    );
  })();

  const sectionBody = (key: SectionKey) => {
    switch (key) {
      case "colours":
        return (
          <div className={styles.czSectionBody}>
            {sourceKind === "brand" && (
              <p className={styles.czHint}>Changes to brand template colours are for this apply only.</p>
            )}
            <div className={styles.czSlotGrid}>
              {THEME_SLOTS.map(({ key: slot, label }) => {
                const hex = normalizeHex(draft.colors[slot] ?? undefined) ?? "808080";
                return (
                  <button
                    key={slot}
                    type="button"
                    className={styles.czSlot}
                    aria-label={`${label} #${hex}`}
                    onClick={() => setPicker({ kind: "slot", key: slot, label })}
                  >
                    <span className={styles.czSlotSwatch} style={{ backgroundColor: `#${hex}` }} />
                    <span className={styles.czSlotLabel}>{label}</span>
                  </button>
                );
              })}
            </div>
            <div className={styles.czSecondaryRow}>
              <span className={styles.czFieldLabel}>Secondary</span>
              {secondary.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className={styles.czGallerySwatch}
                  style={{ backgroundColor: `#${hex}` }}
                  aria-label={`Secondary colour #${hex}`}
                  onClick={() => setPicker({ kind: "secondary-edit", hex })}
                />
              ))}
              <button type="button" className={styles.czAddSwatch} aria-label="Add secondary colour" onClick={() => setPicker({ kind: "secondary-add" })}>
                +
              </button>
            </div>
            {pickerNode}
          </div>
        );
      case "fonts":
        return (
          <div className={styles.czSectionBody}>
            <FontCombobox
              label="Heading"
              value={draft.fonts.titleFont}
              fonts={fonts}
              themeFonts={[{ label: "Headings", name: draft.fonts.titleFont }, { label: "Body", name: draft.fonts.bodyFont }]}
              onChange={setHeadingFont}
            />
            <FontCombobox
              label="Body"
              value={draft.fonts.bodyFont}
              fonts={fonts}
              themeFonts={[{ label: "Headings", name: draft.fonts.titleFont }, { label: "Body", name: draft.fonts.bodyFont }]}
              onChange={setBodyFont}
            />
            {missingFonts.map((font) => (
              <output key={font} className={styles.czWarn}>
                <b>{font}</b> isn&rsquo;t installed on this device. PowerPoint will show a similar font instead, so text may look
                different. Install the font, or choose another one.
              </output>
            ))}
          </div>
        );
      case "title":
        return renderFormat("title", true);
      case "body":
        return renderFormat("body", false);
      case "subtitle":
        return renderFormat("subtitle", true);
      case "footnote":
        return renderFormat("footnote", true);
      default:
        return (
          <div className={styles.czSectionBody}>
            {([
              ["allowRoundedCorners", "Keep rounded corners"],
              ["allowShadows", "Keep shadows"],
              ["allowAnimations", "Keep animations"],
            ] as const).map(([k, label]) => (
              <label key={k} className={styles.czSwitchRow}>
                <span>{label}</span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={draftAdv[k]}
                  onChange={(e) => setDraftAdv((a) => ({ ...a, [k]: e.target.checked }))}
                />
              </label>
            ))}
          </div>
        );
    }
  };

  const footer = !dirty ? (
    <button type="button" className={styles.applyBtn} onClick={onDone}>Done</button>
  ) : (
    <div className={styles.czFooterRow}>
      <button
        type="button"
        className={styles.applyBtn}
        onClick={() =>
          sourceKind === "brand"
            ? onUseForThisApply(draft, draftAdv, added)
            : onSaveChanges(draft, draftAdv, added)
        }
      >
        {sourceKind === "brand" ? "Use for This Apply" : "Save Changes"}
      </button>
      <button type="button" className={styles.czCancelBtn} onClick={onDiscard}>Cancel</button>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader title="Customise" subtitle="Deckorate" onBack={handleBack} />
        <output className={styles.czInfo}>{INFO_MESSAGE[sourceKind](sourceName)}</output>

        {SECTION_ORDER.map((key) => {
          const isOpen = Boolean(open[key]);
          const missing = unsetSections.has(key as StyleSection);
          const isFirstMissing = missingSections.length > 0 && key === missingSections[0];
          const fontWarn = key === "fonts" && missingFonts.length > 0;
          return (
            <div key={key} className={styles.czSection} ref={isFirstMissing ? firstMissingRef : undefined}>
              <button
                type="button"
                className={styles.czSectionHeader}
                aria-expanded={isOpen}
                aria-controls={`customise-${key}`}
                onClick={() => toggle(key)}
              >
                <span className={styles.czChevron} aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
                <span className={styles.czSectionTitle}>{SECTION_TITLES[key]}</span>
                {fontWarn && (
                  <span className={styles.czWarnIcon}>
                    <span aria-hidden="true">⚠</span>
                    <span className={styles.srOnly}>Font not installed</span>
                  </span>
                )}
                {missing && <span className={styles.czNotSet}>NOT SET</span>}
                <span className={styles.czSectionSummary}>{summaries[key]}</span>
              </button>
              {isOpen && <div id={`customise-${key}`}>{sectionBody(key)}</div>}
            </div>
          );
        })}
      </div>

      <div className={styles.applyBar}>{footer}</div>

      {leaveOpen && (
        <ConfirmDialog
          title="Discard your changes?"
          safeLabel="Keep Editing"
          confirmLabel="Discard"
          isDanger
          onSafe={() => setLeaveOpen(false)}
          onConfirm={() => { setLeaveOpen(false); onDiscard(); }}
        >
          Your changes to <b>{sourceName}</b> haven&rsquo;t been saved yet.
        </ConfirmDialog>
      )}
    </div>
  );
}

/** Numeric font size field (BRD FR-45): 1 to 400 pt, steppers and arrow keys
 *  through the standard sizes. "–" while unset. An invalid value is rejected
 *  with its message and the field returns to the previous value (EC-24). */
function SizeField({ label, value, onChange }: Readonly<{ label: string; value: number | null; onChange: (v: number) => void }>) {
  const [text, setText] = useState(value === null ? "–" : String(value));
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  useEffect(() => {
    setText(value === null ? "–" : String(value));
  }, [value]);

  const commit = () => {
    const result = validateSize(text);
    if ("error" in result) {
      setError(result.error);
      setText(value === null ? "–" : String(value));
      return;
    }
    setError(null);
    if (result.value !== value) onChange(result.value);
  };

  const step = (direction: 1 | -1) => {
    const base = value ?? (direction === 1 ? 7 : 9);
    const next = stepSize(base, direction);
    setError(null);
    setText(String(next));
    onChange(next);
  };

  return (
    <div className={styles.czFieldRow}>
      <label className={styles.czFieldLabel} htmlFor={inputId}>{label}</label>
      <div className={styles.czSizeWrap}>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          maxLength={3}
          className={styles.czSizeInput}
          aria-label={`${label} (pt)`}
          aria-invalid={error !== null}
          value={text}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => { setText(e.target.value); setError(null); }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") { e.preventDefault(); step(1); }
            else if (e.key === "ArrowDown") { e.preventDefault(); step(-1); }
            else if (e.key === "Enter") { e.preventDefault(); commit(); }
          }}
        />
        <button type="button" className={styles.czStepper} aria-label={`Increase ${label.toLowerCase()}`} onClick={() => step(1)}>▲</button>
        <button type="button" className={styles.czStepper} aria-label={`Decrease ${label.toLowerCase()}`} onClick={() => step(-1)}>▼</button>
        <span className={styles.czUnit}>pt</span>
      </div>
      {error && <div className={styles.czFieldError} role="alert">{error}</div>}
    </div>
  );
}

// ─── Reading checklist ─────────────────────────────────────────────────────

/** Reading-progress checklist shown while an uploaded template is being
 * parsed (BRD FR-28) — the extractor itself is a single async call with no
 * granular phase signal, so these steps advance on a timer purely to give the
 * user a sense of progress; they all resolve to "done" once reading finishes. */
export const READING_STEPS = [
  "Opening the file",
  "Finding theme colours",
  "Reading fonts",
  "Checking text styles",
  "Building a preview",
];

export function ReadingChecklist({ filename }: Readonly<{ filename: string }>) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setStepIndex(0);
    const id = setInterval(() => {
      setStepIndex((i) => (i < READING_STEPS.length - 1 ? i + 1 : i));
    }, 550);
    return () => clearInterval(id);
  }, [filename]);

  return (
    <div className={styles.extractChecklist}>
      <div className={styles.extractChecklistTitle}>Reading {filename}…</div>
      <ul className={styles.extractChecklistList}>
        {READING_STEPS.map((label, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          let icon = "○";
          if (done) icon = "✓";
          else if (active) icon = "●";
          return (
            <li
              key={label}
              className={[
                styles.extractChecklistItem,
                done ? styles.extractChecklistDone : "",
                active ? styles.extractChecklistActive : "",
              ].filter(Boolean).join(" ")}
            >
              <span className={styles.extractChecklistIcon} aria-hidden="true">
                {icon}
              </span>
              {label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── This Deck's Theme tab ─────────────────────────────────────────────────

export type DeckLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; masters: readonly MasterInfo[] };

interface ThemeTabProps {
  readonly load: DeckLoadState;
  readonly selectedMasterId: string | null;
  readonly scope: "slide" | "deck";
  readonly removeOthers: boolean;
  readonly onRemoveOthersChange: (value: boolean) => void;
  readonly savedMasterIds: ReadonlySet<string>;
  readonly appliedMasterId: string | null;
  readonly onSelectMaster: (master: MasterInfo) => void;
  readonly onCustomise: (master: MasterInfo) => void;
  readonly onSave: (master: MasterInfo) => void;
  readonly onBrowseTemplates: () => void;
  /** Notes (read confirmation, missing styles, fonts) rendered under the selected master. */
  readonly renderNotes: (master: MasterInfo) => ReactNode;
  readonly disabled: boolean;
}

/** This Deck's Theme tab (BRD section 5.4): the open presentation's own slide
 *  master as the brand source — one master as a single card, several as a
 *  radio list; a deck that only uses the Office default theme has no brand. */
export function ThemeTab({
  load, selectedMasterId, scope, removeOthers, onRemoveOthersChange, savedMasterIds, appliedMasterId,
  onSelectMaster, onCustomise, onSave, onBrowseTemplates, renderNotes, disabled,
}: Readonly<ThemeTabProps>) {
  if (load.status === "loading") {
    return <div className={styles.savedTemplatesStatus}>Reading this deck…</div>;
  }
  if (load.status === "error") {
    return (
      <BrandAlert tone="error" title="Couldn't read this presentation.">
        {load.message}
      </BrandAlert>
    );
  }

  const brand = brandMasters(load.masters);
  if (brand.length === 0) {
    return (
      <BrandAlert
        tone="info"
        title="This presentation doesn't have an active theme."
        actions={[{ label: "Browse Brand Templates", onClick: onBrowseTemplates }]}
      >
        It uses the default Office Theme, so there&rsquo;s no brand to apply. Choose a brand template, or upload one.
      </BrandAlert>
    );
  }

  const renderSave = (master: MasterInfo) =>
    savedMasterIds.has(master.id) ? null : (
      <button type="button" className={styles.sessionCardSaveBtn} disabled={disabled} onClick={() => onSave(master)}>
        Save
      </button>
    );

  if (brand.length === 1) {
    const master = brand[0]!;
    const selected = selectedMasterId === master.id;
    return (
      <>
        <div className={`${styles.themeCard} ${selected ? styles.themeCardSelected : ""}`}>
          <button
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={master.name}
            className={styles.cardRadioButton}
            disabled={disabled}
            onClick={() => onSelectMaster(master)}
          >
            <span className={`${styles.themeCardDot} ${selected ? styles.themeCardDotOn : ""}`} aria-hidden="true" />
            <span className={styles.themeCardText}>
              <span className={styles.themeCardTitle} title={master.name}>{truncateName(master.name)}</span>
              <span className={styles.themeCardDesc}>Slide master · {master.layoutCount} layouts</span>
            </span>
          </button>
          {renderSave(master)}
          <MoreMenu itemName={master.name} disabled={disabled} items={[{ key: "customise", label: "Customise", onSelect: () => onCustomise(master) }]} />
        </div>
        {selected && renderNotes(master)}
        {selected && appliedMasterId === master.id && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
      </>
    );
  }

  const selectedMaster = brand.find((m) => m.id === selectedMasterId) ?? null;
  return (
    <>
      <BrandAlert tone="info">
        This presentation has {brand.length} slide masters. Choose the one every slide should follow.
      </BrandAlert>
      <div className={styles.savedTemplatesTitle}>SLIDE MASTERS</div>
      <div role="radiogroup" aria-label="Slide masters" className={styles.savedTemplatesList}>
        {brand.map((master) => {
          const selected = selectedMasterId === master.id;
          return (
            <div key={master.id} className={`${styles.savedTemplateRow} ${selected ? styles.savedTemplateRowSelected : ""}`}>
              <div className={styles.savedTemplateRowTop}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={master.name}
                  className={styles.cardRadioButton}
                  disabled={disabled}
                  onClick={() => onSelectMaster(master)}
                >
                  <span className={`${styles.themeCardDot} ${selected ? styles.themeCardDotOn : ""}`} aria-hidden="true" />
                  <span className={styles.themeCardText}>
                    <span className={styles.themeCardTitle} title={master.name}>{truncateName(master.name)}</span>
                    <span className={styles.themeCardDesc}>
                      {master.layoutCount} layouts · used on {master.slideCount} slide{master.slideCount === 1 ? "" : "s"}
                    </span>
                  </span>
                </button>
                {selected && renderSave(master)}
                <MoreMenu itemName={master.name} disabled={disabled} items={[{ key: "customise", label: "Customise", onSelect: () => onCustomise(master) }]} />
              </div>
              {selected && renderNotes(master)}
              {selected && appliedMasterId === master.id && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
            </div>
          );
        })}
      </div>
      {scope === "deck" && selectedMaster && (
        <label className={styles.removeOthers}>
          <input type="checkbox" checked={removeOthers} onChange={(e) => onRemoveOthersChange(e.target.checked)} />
          <span>
            Remove the other masters from this presentation{" "}
            <span className={styles.removeOthersHint}>Slides that use them will switch to {selectedMaster.name}.</span>
          </span>
        </label>
      )}
    </>
  );
}

// ─── Upload tab ────────────────────────────────────────────────────────────

/** One uploaded file for this PowerPoint session (BRD FR-30). */
export interface UploadEntry {
  readonly id: string;
  readonly name: string;
  readonly buffer: ArrayBuffer;
  /** Brand themes in the file (the Office default theme is never listed). */
  readonly themes: readonly MasterInfo[];
  /** Swatches of the first theme, for the card. */
  readonly swatches: readonly string[];
}

export interface UploadProblem {
  readonly tone: AlertTone;
  readonly title: string;
  readonly text: string;
  /** Show Try Again, which reads the same file again. */
  readonly isRetry: boolean;
}

export interface DuplicatePrompt {
  readonly name: string;
}

export const uploadKey = (uploadId: string, masterId: string) => `${uploadId}:${masterId}`;

interface UploadTabProps {
  readonly uploads: readonly UploadEntry[];
  readonly selectedKey: string | null;
  readonly appliedKey: string | null;
  readonly reading: string | null;
  readonly problem: UploadProblem | null;
  readonly duplicate: DuplicatePrompt | null;
  readonly savedKeys: ReadonlySet<string>;
  readonly renameRequestId: string | null;
  readonly fileInputRef: RefObject<HTMLInputElement>;
  readonly onFiles: (files: FileList | File[] | null) => void;
  readonly onSelect: (upload: UploadEntry, theme: MasterInfo) => void;
  readonly onCustomise: (upload: UploadEntry, theme: MasterInfo) => void;
  readonly onSave: (upload: UploadEntry, theme: MasterInfo) => void;
  readonly onRemove: (upload: UploadEntry) => void;
  readonly onRename: (id: string, name: string) => "ok" | "duplicate";
  readonly onRenameDone: () => void;
  readonly onRetry: () => void;
  readonly onDismissProblem: () => void;
  readonly onDuplicateChoice: (choice: "cancel" | "keep-both" | "replace") => void;
  readonly renderNotes: (upload: UploadEntry, theme: MasterInfo) => ReactNode;
}

/** Upload tab (BRD section 5.5): drop area, reading progress, errors, and the
 *  list of this session's uploads — each with Save and a More options (⋯) menu. */
export function UploadTab(props: Readonly<UploadTabProps>) {
  const {
    uploads, selectedKey, appliedKey, reading, problem, duplicate, savedKeys, renameRequestId, fileInputRef,
    onFiles, onSelect, onCustomise, onSave, onRemove, onRename, onRenameDone, onRetry, onDismissProblem,
    onDuplicateChoice, renderNotes,
  } = props;
  const busy = reading !== null;
  const [dragging, setDragging] = useState(false);
  // The empty drop area: at the top before anything is uploaded, below the uploaded list afterwards.
  const dropArea = (
    <>
          <button
            type="button"
            className={`${styles.uploadZone} ${dragging ? styles.uploadZoneDrag : ""}`}
            disabled={busy}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              onFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <span className={styles.uploadIcon} aria-hidden="true">📄</span>
            <span className={styles.uploadLabel}>Drop a template here, or browse</span>
            <span className={styles.uploadHint}>A PowerPoint file (.pptx or .potx), up to 50 MB.</span>
          </button>
          <p className={styles.uploadHint}>Your uploads stay on this device. Only you can see and remove them.</p>
      </>
  );

  return (
    <div className={styles.sourcePane}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pptx,.potx,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.presentationml.template"
        aria-label="Upload brand template PowerPoint file"
        className={styles.uploadInput}
        onChange={(e) => {
          onFiles(e.target.files);
          // Allow choosing the same file again after an error or Cancel.
          e.target.value = "";
        }}
      />

      {uploads.length === 0 && dropArea}

      {reading && <ReadingChecklist filename={reading} />}

      {problem && (
        <BrandAlert
          tone={problem.tone}
          title={problem.title}
          actions={problem.isRetry ? [{ label: "Try Again", onClick: onRetry }] : []}
          onClose={onDismissProblem}
        >
          {problem.text}
        </BrandAlert>
      )}

      {uploads.length > 0 && (
        <>
          <div className={styles.savedTemplatesHeader}>
            <span className={styles.savedTemplatesTitle}>
              UPLOADED TEMPLATES <span className={styles.savedTemplatesManaged}>&ndash; available until PowerPoint closes</span>
            </span>
          </div>
          <div role="radiogroup" aria-label="Uploaded templates">
<ul className={styles.savedTemplatesList}>
            {uploads.map((upload) => (
              <UploadRow
                key={upload.id}
                upload={upload}
                busy={busy}
                selectedKey={selectedKey}
                appliedKey={appliedKey}
                savedKeys={savedKeys}
                isRenameRequested={renameRequestId === upload.id}
                onSelect={onSelect}
                onCustomise={onCustomise}
                onSave={onSave}
                onRemove={onRemove}
                onRename={onRename}
                onRenameDone={onRenameDone}
                renderNotes={renderNotes}
              />
            ))}
          </ul>
</div>
        </>
      )}

      {uploads.length > 0 && dropArea}

      {duplicate && (
        <ConfirmDialog
          title="This file is already uploaded."
          safeLabel="Cancel"
          confirmLabel="Replace"
          extraActions={[{ label: "Keep Both", onClick: () => onDuplicateChoice("keep-both") }]}
          onSafe={() => onDuplicateChoice("cancel")}
          onConfirm={() => onDuplicateChoice("replace")}
        >
          <b>{duplicate.name}</b>
          <br />
          Replace the one in your uploads, or keep both.
        </ConfirmDialog>
      )}
    </div>
  );
}

function UploadRow({
  upload, busy, selectedKey, appliedKey, savedKeys, isRenameRequested, onSelect, onCustomise, onSave, onRemove,
  onRename, onRenameDone, renderNotes,
}: Readonly<{
  upload: UploadEntry;
  busy: boolean;
  selectedKey: string | null;
  appliedKey: string | null;
  savedKeys: ReadonlySet<string>;
  isRenameRequested: boolean;
  onSelect: (upload: UploadEntry, theme: MasterInfo) => void;
  onCustomise: (upload: UploadEntry, theme: MasterInfo) => void;
  onSave: (upload: UploadEntry, theme: MasterInfo) => void;
  onRemove: (upload: UploadEntry) => void;
  onRename: (id: string, name: string) => "ok" | "duplicate";
  onRenameDone: () => void;
  renderNotes: (upload: UploadEntry, theme: MasterInfo) => ReactNode;
}>) {
  const multi = upload.themes.length > 1;
  const first = upload.themes[0]!;
  const selectedTheme = upload.themes.find((t) => selectedKey === uploadKey(upload.id, t.id)) ?? null;
  const selected = selectedTheme !== null;
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(upload.name);
  const [duplicateName, setDuplicateName] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenameRequested) {
      setDraft(upload.name);
      setDuplicateName(false);
      setRenaming(true);
    }
  }, [isRenameRequested]);

  useEffect(() => {
    if (!renaming) return;
    const input = inputRef.current;
    input?.focus();
    input?.setSelectionRange(0, upload.name.lastIndexOf(".") > 0 ? upload.name.lastIndexOf(".") : upload.name.length);
  }, [renaming]);

  const commitRename = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === upload.name) {
      setRenaming(false);
      onRenameDone();
      return;
    }
    if (onRename(upload.id, trimmed) === "duplicate") {
      setDuplicateName(true);
      return;
    }
    setRenaming(false);
    onRenameDone();
  };

  return (
    <li className={`${styles.savedTemplateRow} ${selected ? styles.savedTemplateRowSelected : ""}`}>
      <div className={styles.savedTemplateRowTop}>
        {renaming ? (
          <input
            ref={inputRef}
            type="text"
            className={`${styles.renameInput} ${duplicateName ? styles.renameInputInvalid : ""}`}
            aria-label={`Rename ${upload.name}`}
            aria-invalid={duplicateName}
            maxLength={80}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDuplicateName(false);
            }}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setRenaming(false);
                onRenameDone();
              }
            }}
          />
        ) : (
          <label className={styles.savedTemplateRowMain} title={upload.name}>
            <input
              type="radio"
              name="uploaded-template"
              className={styles.savedTemplateRadio}
              checked={selected}
              disabled={busy}
              onChange={() => onSelect(upload, selectedTheme ?? first)}
            />
            <span className={styles.savedTemplateName}>{truncateName(upload.name)}</span>
            {upload.swatches.length > 0 && !multi && (
              <span className={styles.themeCardSwatches}>
                {upload.swatches.map((hex) => (
                  <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} title={`#${hex}`} />
                ))}
              </span>
            )}
          </label>
        )}
        {!multi && !savedKeys.has(uploadKey(upload.id, first.id)) && (
          <button type="button" className={styles.sessionCardSaveBtn} disabled={busy} onClick={() => onSave(upload, first)}>
            Save
          </button>
        )}
        <MoreMenu
          itemName={upload.name}
          disabled={busy}
          items={[
            // A multi-theme file's own menu has Remove from uploads only; each theme has its own Customise.
            ...(multi ? [] : [{ key: "customise", label: "Customise", onSelect: () => onCustomise(upload, first) }]),
            { key: "remove", label: "Remove from uploads", onSelect: () => onRemove(upload) },
          ]}
        />
      </div>
      {renaming && duplicateName && (
        <div className={styles.fieldError} role="alert">
          A template with this name already exists. Enter a different name.
        </div>
      )}
      {multi && <div className={styles.uploadHint}>Contains {upload.themes.length} themes</div>}

      {selected && multi && (
        <div className={styles.themeList}>
          <div className={styles.uploadHint}>Choose a theme to use. Save any theme to keep it.</div>
          <div role="radiogroup" aria-label={`Themes in ${upload.name}`}>
            {upload.themes.map((theme) => {
              const on = selectedKey === uploadKey(upload.id, theme.id);
              return (
                <div key={theme.id} className={styles.savedTemplateRowTop}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={theme.name}
                    className={styles.cardRadioButton}
                    disabled={busy}
                    onClick={() => onSelect(upload, theme)}
                  >
                    <span className={`${styles.themeCardDot} ${on ? styles.themeCardDotOn : ""}`} aria-hidden="true" />
                    <span className={styles.themeCardTitle} title={theme.name}>{truncateName(theme.name)}</span>
                  </button>
                  {!savedKeys.has(uploadKey(upload.id, theme.id)) && (
                    <button type="button" className={styles.sessionCardSaveBtn} disabled={busy} onClick={() => onSave(upload, theme)}>
                      Save
                    </button>
                  )}
                  <MoreMenu
                    itemName={theme.name}
                    disabled={busy}
                    items={[{ key: "customise", label: "Customise", onSelect: () => onCustomise(upload, theme) }]}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedTheme && renderNotes(upload, selectedTheme)}
      {selectedTheme && appliedKey === selectedKey && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
    </li>
  );
}

// ─── Apply bar ─────────────────────────────────────────────────────────────

export type Scope = "slide" | "deck";
export type SaveTarget = "new" | "current";

export interface BarNotice {
  readonly tone: AlertTone;
  readonly text: string;
  readonly title?: string;
  readonly actions?: readonly { label: string; onClick: () => void }[];
}

const SCOPE_NOTE: Record<Scope, string> = {
  slide: "The brand is applied to the current slide only. The rest of the presentation is unchanged.",
  deck: "This presentation will be updated with the new brand.",
};

const SAVE_TARGET_NOTE: Record<SaveTarget, string> = {
  new: "A branded copy is created. This presentation stays as it is.",
  current: "This presentation is updated directly with the new brand.",
};

export function getApplyLabel(failed: boolean): string {
  return failed ? "Retry Apply Brand" : "Apply Brand";
}

interface ApplyBarProps {
  readonly scope: Scope;
  readonly onScopeChange: (scope: Scope) => void;
  readonly saveTarget: SaveTarget;
  readonly onSaveTargetChange: (target: SaveTarget) => void;
  readonly sourceName: string;
  readonly applyState: ApplyState;
  readonly onCancel: () => void;
  readonly onApply: () => void;
  readonly disabled: boolean;
  readonly disabledReason: string | undefined;
  readonly notice: BarNotice | null;
  readonly onCloseNotice: () => void;
  /** Text appended to the success message for styles that stayed as they were (FR-62). */
  readonly keptStylesText: string | null;
  /** Shown above the Apply button (a brand template's fonts aren't installed — EC-12). */
  readonly aboveButton?: ReactNode;
  /** Lines for the report: options that made the apply skip something (FR-66). */
  readonly skippedActions: readonly string[];
  /** Total slides in the presentation, for "All N slides now use …" (FR-60). */
  readonly slideCount: number | null;
}

/** The docked apply bar (BRD sections 5.7 and 5.8): scope, destination,
 *  progress with Cancel, success / error / cancelled messages with the report,
 *  and the Apply button. */
export function ApplyBar(props: Readonly<ApplyBarProps>) {
  const {
    scope, onScopeChange, saveTarget, onSaveTargetChange, sourceName, applyState, onCancel, onApply, disabled,
    disabledReason, notice, onCloseNotice, keptStylesText, aboveButton, skippedActions, slideCount,
  } = props;
  const failed = applyState.status === "error";
  const applying = applyState.status === "applying";

  return (
    <div className={styles.applyBar}>
      {notice && (
        <BrandAlert tone={notice.tone} title={notice.title} actions={notice.actions} onClose={onCloseNotice}>
          {notice.text}
        </BrandAlert>
      )}

      <div className={styles.actionBarLabel}>APPLY TO</div>
      <div className={styles.seg}>
        <button
          type="button"
          className={`${styles.segBtn} ${scope === "deck" ? styles.segBtnActive : ""}`}
          data-cmd-action="brand-apply-scope-deck"
          aria-pressed={scope === "deck"}
          disabled={applying}
          onClick={() => onScopeChange("deck")}
        >
          Whole Presentation
        </button>
        <button
          type="button"
          className={`${styles.segBtn} ${scope === "slide" ? styles.segBtnActive : ""}`}
          data-cmd-action="brand-apply-scope-slide"
          aria-pressed={scope === "slide"}
          disabled={applying}
          onClick={() => onScopeChange("slide")}
        >
          Just This Slide
        </button>
      </div>

      {scope === "deck" && (
        <>
          <div className={styles.actionBarLabel}>SAVE RESULT TO</div>
          <div className={styles.seg}>
            <button
              type="button"
              className={`${styles.segBtn} ${saveTarget === "new" ? styles.segBtnActive : ""}`}
              aria-pressed={saveTarget === "new"}
              disabled={applying}
              onClick={() => onSaveTargetChange("new")}
            >
              New Presentation
            </button>
            <button
              type="button"
              className={`${styles.segBtn} ${saveTarget === "current" ? styles.segBtnActive : ""}`}
              aria-pressed={saveTarget === "current"}
              disabled={applying}
              onClick={() => onSaveTargetChange("current")}
            >
              This Presentation
            </button>
          </div>
        </>
      )}

      <div className={styles.scopeNote}>
        <svg aria-hidden="true" viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.scopeNoteIcon}>
          <circle cx="10" cy="10" r="7.5" />
          <path d="M10 9v4M10 6.5v.5" />
        </svg>
        <span>{scope === "slide" ? SCOPE_NOTE.slide : SAVE_TARGET_NOTE[saveTarget]}</span>
      </div>

      <ApplyStatus
        applyState={applyState}
        scope={scope}
        saveTarget={saveTarget}
        sourceName={sourceName}
        onCancel={onCancel}
        keptStylesText={keptStylesText}
        skippedActions={skippedActions}
        slideCount={slideCount}
        onApply={onApply}
      />

      {aboveButton}

      <button
        type="button"
        className={styles.applyBtn}
        data-cmd-action="brand-apply-confirm"
        onClick={onApply}
        aria-disabled={disabled}
        disabled={disabled}
        title={disabled && !applying ? disabledReason : undefined}
      >
        {applying ? "Applying…" : getApplyLabel(failed)}
      </button>
    </div>
  );
}

function ApplyStatus({
  applyState, scope, saveTarget, sourceName, onCancel, keptStylesText, skippedActions, slideCount,
}: Readonly<{
  applyState: ApplyState;
  scope: Scope;
  saveTarget: SaveTarget;
  sourceName: string;
  onCancel: () => void;
  onApply: () => void;
  keptStylesText: string | null;
  skippedActions: readonly string[];
  slideCount: number | null;
}>) {
  if (applyState.status === "applying") {
    const total = scope === "slide" ? 1 : Math.max(1, applyState.total);
    const slide = scope === "slide" ? 1 : Math.min(total, applyState.current + 1);
    const rawPct = applyState.total > 0 ? (applyState.current / applyState.total) * 100 : 0;
    const progressPct = Math.min(100, Math.max(0, Math.round(rawPct)));
    return (
      <div className={styles.progressWrap}>
        <div className={styles.progressRow}>
          <p className={styles.progressMsg} aria-live="polite">
            Applying {sourceName}… slide {slide} of {total} · {applyState.stage}
          </p>
          <button type="button" className={styles.cancelApplyBtn} onClick={onCancel}>
            Cancel
          </button>
        </div>
        <progress className={styles.progressBar} max={100} value={progressPct} aria-label={`Applying ${sourceName}`} />
      </div>
    );
  }

  if (applyState.status === "cancelled") {
    return (
      <BrandAlert tone="info">
        {applyState.rolledBack
          ? "Apply cancelled – nothing was changed."
          : "Apply cancelled. Some slides may already have been updated."}
      </BrandAlert>
    );
  }

  if (applyState.status === "done") {
    return (
      <ApplySuccess
        result={applyState.result}
        scope={scope}
        saveTarget={saveTarget}
        keptStylesText={keptStylesText}
        skippedActions={skippedActions}
        slideCount={slideCount}
      />
    );
  }

  if (applyState.status === "error") {
    return <ApplyError state={applyState} />;
  }

  return null;
}

function ApplyError({ state }: Readonly<{ state: Extract<ApplyState, { status: "error" }> }>) {
  switch (state.kind) {
    case "readonly":
      return (
        <BrandAlert tone="error" title="This presentation can't be edited.">
          It&rsquo;s read-only, so nothing was changed. Make it editable in PowerPoint, then try again.
        </BrandAlert>
      );
    case "connection":
      return (
        <BrandAlert tone="error" title="Connection lost.">
          Nothing was changed in your presentation. Check your connection, then try again.
        </BrandAlert>
      );
    case "interrupted":
      return (
        <BrandAlert tone="error" title={`Apply interrupted at slide ${state.slide ?? 1} of ${state.total ?? 1}.`}>
          PowerPoint stopped responding part-way through. Everything was put back the way it was, so nothing has changed.
          Press Apply Brand to try again.
        </BrandAlert>
      );
    default:
      return (
        <BrandAlert tone="error" title="Couldn't apply the brand.">
          {state.message}
        </BrandAlert>
      );
  }
}

/** Success message (BRD FR-59 to FR-64): this slide / presentation / copy, the
 *  styles that were kept, View Report, and ✕. */
function ApplySuccess({
  result, scope, saveTarget, keptStylesText, skippedActions,
}: Readonly<{
  result: ApplyBrandResult;
  scope: Scope;
  saveTarget: SaveTarget;
  keptStylesText: string | null;
  skippedActions: readonly string[];
  slideCount: number | null;
}>) {
  const [dismissed, setDismissed] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const isCopy = scope === "deck" && saveTarget === "new";

  // A fresh apply always produces a new `result` object, so resetting on that
  // reference re-opens the message for the next apply.
  useEffect(() => {
    setDismissed(false);
    setReportOpen(false);
  }, [result]);

  const handleViewSlides = useCallback(() => {
    setDismissed(true);
    const target = scope === "deck" ? 1 : result.changedSlideIndex;
    if (target) goToSlide(target).catch((e) => logOfficeError(e, "apply-brand-view-slides"));
  }, [scope, result.changedSlideIndex]);

  if (dismissed) return null;

  const title = isCopy ? "Branded copy opened in a new window" : "Brand applied";
  const caption = isCopy
    ? "Save it to choose where it goes. The original file stays as it was."
    : "This file was updated with the new brand.";

  return (
    <>
      <div className={`${styles.resultBox} ${styles.resultSuccess}`}>
        <div className={styles.resultHeader}>
          <span className={styles.resultHeaderTitle}>
            <span aria-hidden="true">✓</span> {title}
          </span>
          <button type="button" className={styles.resultDismiss} aria-label="Dismiss message" onClick={() => setDismissed(true)}>
            ×
          </button>
        </div>
        <p className={styles.resultCaption}>
          {caption}
          {keptStylesText ? ` ${keptStylesText}` : ""}
        </p>
        {result.actions.length > 0 && (
          <ul className={styles.resultActions}>
            {result.actions.map((a) => <li key={a}>{a}</li>)}
          </ul>
        )}
        <div className={styles.resultFooter}>
          {!isCopy && (
            <button type="button" className={styles.viewSlidesBtn} onClick={handleViewSlides}>
              View the Slides
            </button>
          )}
          <button type="button" className={styles.viewReportLink} onClick={() => setReportOpen(true)}>
            View report
          </button>
        </div>
      </div>
      {reportOpen && <BrandReportModal result={result} skipped={skippedActions} onClose={() => setReportOpen(false)} />}
    </>
  );
}

/** Brand Application Report (BRD FR-66): counts, a ticked list of the actions
 *  taken and any actions skipped by an option, closed with Got it. */
function BrandReportModal({ result, skipped, onClose }: Readonly<{ result: ApplyBrandResult; skipped: readonly string[]; onClose: () => void }>) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Native <dialog> owns focus trapping, Escape-to-close and the modal a11y
  // tree for free — the "close" event (fired for both Escape and
  // dialog.close()) is the single place we sync back to React state. Some
  // older Office WebView hosts (and jsdom under test) don't implement
  // showModal()/close(), so fall back to the plain `open` attribute there.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    dialog.addEventListener("close", onClose);
    return () => dialog.removeEventListener("close", onClose);
  }, [onClose]);

  const closeDialog = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
      onClose();
    }
  }, [onClose]);

  return (
    <dialog ref={dialogRef} className={styles.reportModal} aria-label="Brand Application Report">
      <div className={styles.reportHeader}>
        <span className={styles.reportTitle}>Brand Application Report</span>
        <button type="button" className={styles.resultDismiss} aria-label="Close report" onClick={closeDialog}>
          ×
        </button>
      </div>
      <p className={styles.reportSummary}>
        {result.slidesProcessed} slide(s) processed, {result.shapesUpdated} shape(s) updated.
      </p>
      {result.actions.length > 0 ? (
        <ul className={styles.reportList}>
          {result.actions.map((a) => <li key={a}>✓ {a}</li>)}
        </ul>
      ) : (
        <p className={styles.reportEmpty}>No changes were needed — this file already matched the brand.</p>
      )}
      {skipped.length > 0 && (
        <>
          <p className={styles.reportSummary}>Skipped by your options</p>
          <ul className={styles.reportList}>
            {skipped.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </>
      )}
      <button type="button" className={styles.applyBtn} onClick={closeDialog}>
        Got it
      </button>
    </dialog>
  );
}

// ─── Apply Brand settings ──────────────────────────────────────────────────────

const INCLUDED_PARTS: ReadonlyArray<{ name: string; detail: string }> = [
  { name: "Colours", detail: "The brand's colour set: dark, light, accents, links" },
  { name: "Fonts", detail: "Title / body typefaces" },
  { name: "Text styles", detail: "Title / body / subtitle / footnote sizing & colour" },
  { name: "Table styles", detail: "Header fill, header text, banding" },
  { name: "Visual effects", detail: "Shadows, corners, animations aligned to brand rules" },
];

/** The screen behind the header's gear: what every apply covers. Choosing which
 *  parts to include is planned and not available yet. */
export function SettingsScreen({ onBack }: Readonly<{ onBack: () => void }>) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader title="Apply Brand Settings" onBack={onBack} backLabel="Back" />
        <p className={styles.settingsIntro}>
          Apply Brand applies every part of the brand: colours, fonts, text styles, tables and effects. Choosing which parts
          to include is planned and not available yet.
        </p>
        <div className={styles.settingsLabel}>INCLUDED IN EVERY APPLY</div>
        <ul className={styles.settingsList}>
          {INCLUDED_PARTS.map((part) => (
            <li key={part.name} className={styles.settingsRow}>
              <span>
                <span className={styles.settingsName}>{part.name}</span>
                <span className={styles.settingsDetail}>{part.detail}</span>
              </span>
              <span className={styles.settingsAlways}>Always</span>
            </li>
          ))}
        </ul>
        <div className={styles.settingsNote}>A future release will let you turn individual parts on or off per apply.</div>
      </div>
    </div>
  );
}
